<template>
  <div class="position-relative scheduler" :style="width != null? { 'width': width } : {}">
    <div v-if="showNoRowsOverlay" class="no-rows-overlay">
      <span class='grid-overlay'>{{ noRowsMessage }}</span>
    </div>
    <div v-if="holdUpdateUntilTreeDataChanged" class="loading-overlay">
      <span class='grid-overlay'>{{ 'loading...' }}</span>
    </div>
    <div :id="id" class="dhx_cal_container scheduler-border-part" 
      :style="{ width: '100%', height: `${height}` }"
      @click="onClickScheduler" 
      @mouseover="onMouseOver" 
      @mouseleave="onMouseLeave">
      <div class="dhx_cal_navline" :style="width != null? { 'width': width } : {}">
        <div class="dhx_cal_date"></div>
      </div>
      <div class="dhx_cal_header">
      </div>
      <div class="dhx_cal_data" :style="width != null? { 'width': width } : {}">
      </div>  
    </div>
    
  </div>
</template>

<script>
/**
 * Emit Event:
 * 1. 'taskNew' with parameter: { parentId }
 * 2. 'taskEdit' with parameter: { uuId: id, parentId }
 * 3. 'taskLinkEdit' with parameter: {  taskId, predecessorId } Refer #Register 'taskLinkEdit' emit event
 */
import { cloneDeep, debounce } from 'lodash';

import * as moment from 'moment-timezone';
moment.tz.setDefault('Etc/UTC');
const locale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
moment.locale(locale);

//Vendor (Script) Library 
// DHTMLx
import 'dhtmlx-scheduler';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_limit';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_timeline';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_treetimeline';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_tooltip';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_multisection';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_csp';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_key_nav';
import 'dhtmlx-scheduler/codebase/dhtmlxscheduler_material.css';

// for exporting
import XLSX from 'xlsx';

import { 
  getDateRef, 
  getUsage, 
  toComplimentary,
  invertColor,
  formatTooltipDate,
  EventBus,
  processCalendar,
  transformCalendar,
  costFormat,
  costFormatAdv,
  getCost,
  getDefaultAvailability,
  convertDate,
  msToTime,
  timeToMs
} from '@/helpers';
import { 
  toFixed, calcDateTimeDurationv2, TRIGGERS, roundDurationDisplay,
  convertDisplayToDuration, convertDurationToDisplay, DEFAULT_CALENDAR, extractDurationConversionOpts
} from '@/helpers/task-duration-process';

import { getCustomFieldInfo } from '@/helpers/custom-fields';
import { getWeekNumber, getWeekNumberProjectStart } from '@/helpers';

import { activityService, bookingService, taskService, compositeService, staffService, resourceService,
         companyService, locationService, projectService, stageService, departmentService, skillService } from '@/services';



let tasksMap = {};
let usageCache = {};

const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const UUID_CHECK = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

function getStartAndEndTime(event, calendars, self) {
  let startHour = -1;
  let startMinutes = 0;
  let endHour = -1;
  let endMinutes = 0;
  let currentStart = -1;
  let currentEnd = -1;
  
  // search for the day name matching the date day
  for (const entry of calendars) {
    for (const cal of entry) {
      // sometimes the calendar type is an array and sometimes it is a string
      const type = Array.isArray(cal.type) ? cal.type[0] : cal.type;
      if (typeof cal.startHour !== 'undefined' &&
          type.toLowerCase() === dayNames[event.start_date.getDay()] &&
          (startHour === -1 || timeToMs(cal.startHour) < currentStart)) {
        currentStart = timeToMs(cal.startHour);
        if (typeof cal.startHour === 'string') {
          const cmp = cal.startHour.split(':');
          startHour = parseInt(cmp[0]);
          startMinutes = parseInt(cmp[1]);
        }
        else {
          const cmp = msToTime(cal.startHour).split(':');
          startHour = parseInt(cmp[0]);
          startMinutes = parseInt(cmp[1]);
        }
      }
      if (typeof cal.endHour !== 'undefined' &&
          type.toLowerCase() === dayNames[event.end_date.getDay()] &&
          (endHour === -1 || timeToMs(cal.endHour) > currentEnd)) {
        currentEnd = timeToMs(cal.endHour);
        if (typeof cal.endHour === 'string') {
          const cmp = cal.endHour.split(':');
          endHour = Math.floor(parseInt(cmp[0]));
          endMinutes = parseInt(cmp[1]);
        }
        else {
          const cmp = msToTime(cal.endHour).split(':');
          endHour = parseInt(cmp[0]);
          endMinutes = parseInt(cmp[1]);
        }
      }
      
    }
  }
  
  // not found, use default
  if (startHour === -1) {
    startHour = Math.floor(self.durationConversionOpts.startHour / 1000 / 60 / 60);
    startMinutes = self.durationConversionOpts.startHour / 1000 / 60 % 60;
  }
  if (endHour === -1) {
    endHour = Math.floor(self.durationConversionOpts.closeHour / 1000 / 60 / 60);
    endMinutes = self.durationConversionOpts.closeHour / 1000 / 60 % 60;
  }
  
  return {startHour, startMinutes, endHour, endMinutes};
}

function isDateInThisWeek(date) {
  const todayObj = new Date();
  const todayDate = todayObj.getDate();
  const todayDay = todayObj.getDay();

  // get first date of week
  const firstDayOfWeek = new Date(todayObj.setDate(todayDate - todayDay));

  // get last date of week
  const lastDayOfWeek = new Date(firstDayOfWeek);
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

  // if date is equal or within the first and last dates of the week
  return date >= firstDayOfWeek && date <= lastDayOfWeek;
}

const isToday = (someDate, span) => {
  const today = new Date();
  if (span === 'day') {
    return someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear();
  }
  else if (span === 'week') {
    return isDateInThisWeek(someDate);
  }
  else if (span === 'month') {
    return someDate.getMonth() === today.getMonth() &&
    someDate.getFullYear() === today.getFullYear();
  }
  else if (span === 'year') {
    return someDate.getFullYear() === today.getFullYear();
  }
  return false;
}

function recalcDuration(start_date, end_date, calendar, timescale, staffAlloc, mode, duration, lockDuration, durationConversionOpts) {
  let trigger = TRIGGERS.START_TIME;
  let projectScheduleFromStart = true;
  
  const convertDateToMoment = function(_date) {
    return moment.utc([
      _date.getFullYear()
      , _date.getMonth()
      , _date.getDate()
      , _date.getHours()
      , _date.getMinutes()
      , _date.getSeconds()
    ]);
  }
  const startTime = convertDateToMoment(start_date);
  const closeTime = convertDateToMoment(end_date);
  const defStartHour = msToTime(durationConversionOpts.startHour);
  const defCloseHour = msToTime(durationConversionOpts.closeHour);
  const payload = {
    trigger: trigger
    , startDateStr: startTime.format('YYYY-MM-DD')
    , startTimeStr: timescale === 'day' ? startTime.format('HH:mm') : defStartHour
    , closeDateStr: closeTime.format('YYYY-MM-DD')
    , closeTimeStr: timescale === 'day' ? closeTime.format('HH:mm') : defCloseHour
    , durationDisplay: duration
    , calendar: calendar
    , projScheduleFromStart: projectScheduleFromStart
    , taskAutoScheduleMode: true
    , lockDuration: lockDuration
    , autoMoveForNonWorkingDay: true
    , skipOutOfProjectDateCheck: true
    , "constraintType": null
    , "constraintDateStr": null
    , "projectStartDateStr": null
    , "projectCloseDateStr": null
    , "oldDateStr": null
    , "oldTimeStr": null
    , "resizeMode": mode !== 'move'
    , "disableDurationBuffer": false
    , durationConversionOpts: durationConversionOpts
  }

  const result = calcDateTimeDurationv2(payload);

  let dDisplay = result.durationDisplay; //Note: unit of resultDisplay is always 'D'.
  if (timescale != 'day') {
    dDisplay = roundDurationDisplay(dDisplay, 'D', 1, durationConversionOpts)

  }
  
  if (staffAlloc === 'hours') {
    // convert to hours
    const result = convertDisplayToDuration(dDisplay, durationConversionOpts);
    dDisplay = convertDurationToDisplay(result.value, 'h', durationConversionOpts);

  }
  return {dDisplay, startDate: result.startDateStr, startTime: result.startTimeStr,
          closeDate: result.closeDateStr, closeTime: result.closeTimeStr, value: result.durationDisplay};
}

export default {
  name: 'Scheduler',
  props: {
    startDate: {
      type: String, 
      default: null
    },
    endDate: {
      type: String, 
      default: null
    },
    span: {
      type: String, 
      default: 'day'
    },
    /*treeData: {
      type: Array,
      default: () => []
    },*/
    data: {
      type: Array,
      default: () => []
    },
    staffAlloc: {
      type: String,
      default: 'days'
    },
    height: {
      type: String,
      default: '600px'
    },
    coloring: {
      type: Object,
    },
    permission: {
      type: String,
      default: ''
    },
    deleteEventId: {
      type: [Number, String],
      default: null
    },
    expandLevel: {
      type: Number,
      default: 0
    },
    projectId: {
      type: String,
      default: '000000000000-0000-0000-0000-00000000'
    },
    projectName: {
      type: String,
      default: null
    },
    projectStartDate: {
      type: [Number, String],
      default: null
    },
    disableCreate: {
      type: Boolean,
      default: false
    },
    filterByProject: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Object,
      default: () => { return {} }
    },
    filterFields: {
      type: Array,
      default: () => []
    },
    filterHistory: {
      type: Array,
      default: () => []
    },
    filterFieldValues: {
      type: Object,
      default: () => {}
    },
    resourceField: {
      type: String,
      default: 'staffUuid'
    },
    filterObject: {
      type: Array,
      default: () => []
    },
    markedTimespans: {
      type: Array,
      default: () => []
    },
    showOptimal: {
      type: Boolean,
      default: false
    },
    showUnders: {
      type: Boolean,
      default: false
    },
    showOvers: {
      type: Boolean,
      default: false
    },
    showAdd: {
      type: Boolean,
      default: true
    },
    redraw: {
      type: Boolean,
      default: false
    },
    noRowsMessage: {
      type: String,
      default: null
    },
    showNoRowsOverlay: {
      type: Boolean,
      default: false
    },
    unallocStart: {
      type: String,
      default: null
    },
    unallocEnd: {
      type: String,
      default: null
    },
    redrawEventId: {
      type: String,
      default: null
    },
    expandId: {
      type: String,
      default: null
    },
    collapseId: {
      type: String,
      default: null
    },
    exportToFile: {
      type: Boolean,
      default: false
    },
    scrollY: {
      type: Number,
      default: -1
    },
    width: {
      type: String,
      default: null
    },
    fields: {
      type: Array,
      default: () => []
    },
    customFields: {
      type: Array,
      default: () => []
    },
    companyFields: {
      type: Array,
      default: () => []
    },
    companyCustomFields: {
      type: Array,
      default: () => []
    },
    locationFields: {
      type: Array,
      default: () => []
    },
    locationCustomFields: {
      type: Array,
      default: () => []
    },
    stageFields: {
      type: Array,
      default: () => []
    },
    stageCustomFields: {
      type: Array,
      default: () => []
    },
    projectFields: {
      type: Array,
      default: () => []
    },
    projectCustomFields: {
      type: Array,
      default: () => []
    },
    departmentFields: {
      type: Array,
      default: () => []
    },
    departmentCustomFields: {
      type: Array,
      default: () => []
    },
    skillFields: {
      type: Array,
      default: () => []
    },
    skillCustomFields: {
      type: Array,
      default: () => []
    },
    grouping: {
      type: Object,
      default: () => {}
    },
    skipProcessNodes: {
      type: Boolean,
      default: false
    },
    holdUpdateUntilTreeDataChanged: {
      type: Boolean,
      default: false
    },
    lockPlanner: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: 'scheduler_here'
    },
    isWidget: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      permissionName: 'ACTIVITY',
      selected: [],
      selectedEvents: {},
      draggingEvent: null,
      sin_timer: null,
      clickId: null,
      initialized: false,
      copied: false,
      settingView: false,
      pendingRedrawn: false,
      schedulerInstance: null,
      markedSpans: {},
      durationConversionOpts: {}
    }
  },
  watch: {
    scrollY(newValue) {
      if (this.initialized) {
        /* eslint-disable no-undef */
        const scrollTop = this.schedulerInstance.getView().getScrollPosition();
        if (scrollTop.top !== newValue && newValue != -1) {
          this.schedulerInstance.getView().scrollTo({ top: newValue, left: scrollTop.left });
        }
        /* eslint-enable no-undef */
      }
    },
    lockPlanner(newValue) {
      if (this.disableCreate || newValue) {
        this.schedulerInstance.config.dblclick_create = false;
        this.schedulerInstance.config.drag_create = false;
      }
      else {
        this.schedulerInstance.config.dblclick_create = true;
        this.schedulerInstance.config.drag_create = true;
      }
    },
    exportToFile(newValue) {
      if (newValue) {
        this.fileExport();
      }
    },
    expandId(id) {
      /* eslint-disable no-undef */
      this.schedulerInstance.openSection(id);
      this.$emit('expandedId');
      /* eslint-enable no-undef */
    },
    collapseId(id) {
      /* eslint-disable no-undef */
      this.schedulerInstance.closeSection(id);
      this.$emit('collapsedId');
      /* eslint-enable no-undef */
    },
    redrawEventId(id) {
      if (this.initialized && id) {
        /* eslint-disable no-undef */
        const event = this.schedulerInstance.getEvent(id);
        if (event && event.clones) {
          for (const ev of event.clones) {
            this.schedulerInstance.updateEvent(ev);   // renders the updated event
          }
        }
        this.schedulerInstance.updateEvent(id);   // renders the updated event
        this.$emit('redrawEventComplete');
        /* eslint-enable no-undef */
      }
    },
    'filter.unallocated'(value) {
      if (this.initialized) {
        /* eslint-disable no-undef */
        this.settingView = true;
        this.schedulerInstance.config.event_yoff = this.filter.usage ? 26 : 0;
        
        if (value) {
          // get all events in the time period
          const evs = this.schedulerInstance.getEvents(new Date(this.unallocStart), new Date(this.unallocEnd));
          // only add sections with no events
          this.schedulerInstance.updateCollection("sections", this.treeData.filter(d => evs.filter(e => e.section_id === d.key).length === 0));
        }
        else {
          this.schedulerInstance.updateCollection("sections", this.treeData);
        }
        this.settingView = false;
        /* eslint-enable no-undef */
      }
    },
    'filter.usage'() {
      if (this.initialized) {
        this.pendingRedrawn = false;
        if (!this.holdUpdateUntilTreeDataChanged) {
          setTimeout(() => {
            this.updateData();
            this.$emit('redrawn');
          }, 300);
        }
      }
    },
    'filter.work_hours'() {
      if (this.initialized) {
        /* eslint-disable no-undef */
        this.schedulerInstance.config.first_hour = this.filter.work_hours ? this.filter.work_hours.first_hour : 0;
        this.schedulerInstance.config.last_hour = this.filter.work_hours ? this.filter.work_hours.last_hour : 24; 
        this.pendingRedrawn = false;
        if (!this.holdUpdateUntilTreeDataChanged) {
          setTimeout(() => {
            this.updateData();
            this.$emit('redrawn');
          }, 500);
        }
        /* eslint-enable no-undef */
      }
    },
    redraw(newVal) {
      if (newVal) {
        /* eslint-disable no-undef */
        if (this.initialized) {
          this.schedulerInstance.setCurrentView();
        }
        /* eslint-enable no-undef */
        this.pendingRedrawn = false;
        this.$emit('redrawn');
      }
    },
    span() {
      usageCache = {};
      this.pendingRedrawn = false;
      if (!this.holdUpdateUntilTreeDataChanged) {
        setTimeout(() => {
          this.updateData();
          this.$emit('redrawn');
        }, 500);
      }
    },
    showUnders() {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.setCurrentView();
      }
      /* eslint-enable no-undef */
    },
    showOvers() {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.setCurrentView();
      }
      /* eslint-enable no-undef */
    },
    showOptimal() {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.setCurrentView();
      }
      /* eslint-enable no-undef */
    },
    markedTimespans() {
      if (this.initialized) {
        // this.markedSpans = {};
        //reset markedSpans object by removing all properties
        for (let propKey in this.markedSpans){
          if (Object.hasOwn(this.markedSpans, propKey)){
            delete this.markedSpans[propKey];
          }
        }

        /* eslint-disable no-undef */
        this.schedulerInstance.deleteMarkedTimespan();
        for (const timespan of this.markedTimespans) {
          const date = new Date(timespan.start); // for timezone offset
          const id = this.schedulerInstance.addMarkedTimespan({
                start_date: new Date(timespan.start + (date.getTimezoneOffset() * 60000)),
                end_date: new Date(timespan.end + (date.getTimezoneOffset() * 60000)),
                css: `load-marker-weekend id-${timespan.id}`,
                sections: {
                  timeline: timespan.key
                }
          });
          
          if (!(timespan.id in this.markedSpans)) {
            this.markedSpans[timespan.id] = [];
          }
          this.markedSpans[timespan.id].push({ 
            id: id,
            name: timespan.name, 
            start: timespan.start, 
            end: timespan.end,
            readonly: timespan.readonly,
            key: timespan.key
          });
        }
        if (!this.holdUpdateUntilTreeDataChanged) {
          this.settingView = true;
          this.schedulerInstance.setCurrentView();
          this.settingView = false;
        }
        /* eslint-enable no-undef */
      }
    },
    startDate(newDate) {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.matrix.timeline.x_size = this.getSize();
        if (!this.holdUpdateUntilTreeDataChanged) {
          this.settingView = true;
          this.schedulerInstance.setCurrentView(new Date(newDate));
          this.settingView = false;
        }
      }
      /* eslint-enable no-undef */
    },
    endDate() {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.matrix.timeline.x_size = this.getSize();
        if (!this.holdUpdateUntilTreeDataChanged) {
          this.settingView = true;
          this.schedulerInstance.setCurrentView(new Date(this.startDate));
          this.settingView = false;
        }
      }
      /* eslint-enable no-undef */
    },
    data(newData) {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.clearAll();
        usageCache = {}; // clear the cache so that the values are recalculated
        tasksMap = {};
        if (!this.holdUpdateUntilTreeDataChanged) {
          this.schedulerInstance.parse(newData);
        }
        
      }
      /* eslint-enable no-undef */
    },
    staffAlloc() {
      usageCache = {};
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.setCurrentView();
      }
      /* eslint-enable no-undef */
    },
    coloring() {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance.setCurrentView();
      }
      /* eslint-enable no-undef */
    },
    deleteEventId(id) {
      /* eslint-disable no-undef */
      if (this.initialized) {
        this.schedulerInstance._not_render = true;
        const event = this.schedulerInstance.getEvent(id);
        if (event && event.clones) {
          for (const ev of event.clones) {
            this.schedulerInstance.deleteEvent(ev);   // removes the cloned event
            if (ev in this.markedSpans) {
              for (const span of this.markedSpans[ev]) {
                this.schedulerInstance.deleteMarkedTimespan(span.id);
              }
              delete this.markedSpans[ev];
            }
          }
        }
        this.schedulerInstance.deleteEvent(id);
        if (id in this.markedSpans) {
          for (const span of this.markedSpans[id]) {
            this.schedulerInstance.deleteMarkedTimespan(span.id);
            this.removeSectionCalendar(id, span.key);
          }
          delete this.markedSpans[id];
        }
        this.schedulerInstance.setCurrentView();
        this.schedulerInstance._not_render = false;
        if (this.schedulerInstance._render_wait) this.schedulerInstance.render_view_data();
      }
      /* eslint-enable no-undef */
    },
    expandLevel() {
      /* eslint-disable no-undef */
      if (!this.holdUpdateUntilTreeDataChanged) {
        if (this.initialized) {
          if (this.skipProcessNodes != true) {
            this.processTreeNodes(this.treeData);
          }
          this.schedulerInstance.updateCollection("sections", this.treeData);
        }
        this.schedulerInstance._not_render = false;
        if (this.schedulerInstance._render_wait) this.schedulerInstance.render_view_data();
      }
      /* eslint-enable no-undef */
    }
  },  
  computed: {
    disableDelete() {
      if (this.selected.length === 0) {
        return true;
      }
      
      for (const id of this.selected) {
        /* eslint-disable no-undef */
        const event = this.schedulerInstance.getEvent(id);
        if (event == null) {
          return true;
        }
        /* eslint-enable no-undef */
        if (event.type === 'booking' &&
            !this.canDelete('BOOKING')) {
          return true;   
        }
        else if (event.type === 'activity' &&
            !this.canDelete('ACTIVITY')) {
          return true;   
        }
        else if (event.type === 'task' &&
            !this.canDelete('TASK')) {
          return true;   
        }
        else if (event.type === 'vacation' &&
            !this.canDelete('CALENDAR')) {
          return true;   
        }
      }
      return false;
    }
  },
  mounted() {
    this.permissionName = this.permission;
    this.selected = [];
    const self = this;
    
    /* eslint-disable no-undef */
    this.schedulerInstance.templates.tooltip_date_format=this.schedulerInstance.date.date_to_str("%Y-%m-%d %H:%i"); 
    this.schedulerInstance.locale.labels.timeline_tab = "Timeline";
    this.schedulerInstance.locale.labels.section_custom="Section";
    this.schedulerInstance.config.details_on_create=true;
    this.schedulerInstance.config.details_on_dblclick=true;
    this.schedulerInstance.config.multisection = true;
    this.schedulerInstance.xy.nav_height=0;
    this.schedulerInstance.xy.bar_height=26;
    this.schedulerInstance.config.api_date="%Y-%m-%d";
    this.schedulerInstance.config.time_step = 15;
    this.schedulerInstance.config.limit_time_select = true;
    this.schedulerInstance.config.check_limits = false;
    this.schedulerInstance.config.event_yoff = 26;
    this.schedulerInstance.config.key_nav = true;
    
    this.schedulerInstance.templates.event_class = function(start,end,ev){
      const startRange = new Date(self.startDate).getTime();
      const endRange = new Date(self.endDate).getTime();
      
      // remove the time component from the event start
      const evStart = Date.UTC(start.getFullYear(), start.getMonth(),
                start.getDate(), 0, 0, 0);
      const evEnd = Date.UTC(end.getFullYear(), end.getMonth(),
                end.getDate(), 0, 0, 0);
      const partialStart = evStart < startRange && self.span !== 'year' ? 'partial-start' : '';
      const partialEnd = evEnd > endRange && self.span !== 'year' ? 'partial-end' : '';
      const readonly = self.disablePointer(ev) || self.lockPlanner ? 'readonly' : '';
      
      if (self.selected.includes(ev.id)) {
        return `${readonly} ${partialStart} event_selected ${partialEnd}`;
      }
      else if (!ev.type) {
        return "event_new";
      }
      return `${readonly} ${partialStart} ${partialEnd}`;
    };
    
    if (this.disableCreate || self.lockPlanner) {
      this.schedulerInstance.config.dblclick_create = false;
      this.schedulerInstance.config.drag_create = false;
    }
    else {
      this.schedulerInstance.config.dblclick_create = true;
      this.schedulerInstance.config.drag_create = true;
    }
    
    //===============
    //Configuration
    //=============== 

    //Attch the event before set/update data. Otherwise, the event will be missed.
    self.dataRenderId = self.schedulerInstance.attachEvent('onDataRender', function (){
      self.$emit('schedulerDataRender');
    })
    
    this.updateData();

    //===============
    //Data loading
    //===============
    this.schedulerInstance.config.lightbox.sections=[  
      {name:"description", height:50, map_to:"text", type:"textarea" , focus:true},
      {name:"custom", height:30, type:"timeline", options:null , map_to:"section_id" }, //type should be the same as name of the tab
      {name:"time", height:72, type:"time", map_to:"auto"}
    ];
    
    this.dragEndId = this.schedulerInstance.attachEvent("onDragEnd", function(id, mode){
        // use it to get the object of the dragged event
        const dragged_event = self.schedulerInstance.getEvent(id); 
        const toUpdate = []; 
        
        // reset the step time to prevent selecting the wrong cell on click
        self.schedulerInstance.config.time_step = 15;
        
        if (self.escPressed ||
            !dragged_event) {
          return;
        }

        if (mode == 'move') {
          self.pendingRedrawn = true;
        }
        
        const section = self.schedulerInstance.getSection(dragged_event.section_id);
        if (section.type === 'staff') {
          if (!section.duplicateStaff || (section.duplicateStaff && !section.duplicateStaff.find(s => s.uuId === dragged_event.staffUuid))) {
            dragged_event[self.resourceField] = dragged_event.section_id.slice(dragged_event.section_id.length - 36);
          }
        }
        else if (section.type === 'resource') {
          dragged_event.resourceUuid = dragged_event.section_id.slice(dragged_event.section_id.length - 36);
        }
        else if (dragged_event.section_id !== dragged_event.id) {
          dragged_event.section_id = dragged_event.id;
          self.schedulerInstance.updateEvent(dragged_event.id);  
        }
        
        if (dragged_event.type === 'vacation' &&
            ((dragged_event.start_date.getHours() !== 0 ||
             dragged_event.start_date.getMinutes() !== 0 ||
             dragged_event.start_date.getSeconds() !== 0) ||
             (dragged_event.end_date.getHours() !== 23 ||
             dragged_event.end_date.getMinutes() !== 59 ||
             dragged_event.end_date.getSeconds() !== 0))) {
          const startHours = dragged_event.start_date.getHours();
          let changed = false;
          if (startHours > 12) {
            dragged_event.start_date.setDate(dragged_event.start_date.getDate() + 1);
          }
          const endHours = dragged_event.end_date.getHours();
          if (endHours < 12) {
            dragged_event.end_date.setDate(dragged_event.end_date.getDate() - 1);
          }
          
          if (self.filter.hideNonWorking &&
              self.filter.work_hours.first_hour &&
              self.filter.work_hours.last_hour) {
            if (dragged_event.start_date.getHours() !== self.filter.work_hours.first_hour) {
              dragged_event.start_date.setHours(self.filter.work_hours.first_hour, 0, 0);  // band the vacations to be full days
              changed = true;
            }
            
            if (dragged_event.end_date.getHours() !== self.filter.work_hours.last_hour) {
              dragged_event.end_date.setHours(self.filter.work_hours.last_hour, 0, 0);  // band the vacations to be full days
              changed = true;
            }
          }
          else {
            if (dragged_event.start_date.getHours() !== 0) {
              dragged_event.start_date.setHours(0, 0, 0);  // band the vacations to be full days
              changed = true;
            }
            
            if (dragged_event.end_date.getHours() !== 23) {
              dragged_event.end_date.setHours(23, 59, 0);  // band the vacations to be full days
              changed = true;
            }
          }
          
          if (changed) {
            self.schedulerInstance.updateEvent(dragged_event.id);
          }  
        }
        
        if (typeof id === 'number' &&
            section.type !== 'staff' &&
            section.type !== 'resource') {
          self.schedulerInstance.deleteEvent(id);
          return;    
        }
        
        if (typeof id === 'number' &&
            self.span !== 'day') {
          const start = self.getStartHour(section, dragged_event.start_date.getDay());
          const end = self.getEndHour(section, dragged_event.end_date.getDay());
          const startHour = Math.floor(start / 1000 / 60 / 60);
          const startMinute = start / 1000 / 60 % 60;
          const closeHour = Math.floor(end / 1000 / 60 / 60);
          const closeMinute = end / 1000 / 60 % 60;
          dragged_event.start_date.setHours(startHour, startMinute, 0);  // band the vacations to be full days
          dragged_event.end_date.setHours(closeHour, closeMinute, 0);  // band the vacations to be full days  
        }
        
        if ((section.type === 'staff' ||
             section.type === 'resource' ||
             section.type === 'activity' ||
             section.type === 'booking' ||
             section.type === 'task') &&
            (!self.draggingEvent ||
           (self.draggingEvent.start_date.getTime() !== dragged_event.start_date.getTime() ||
            self.draggingEvent.end_date.getTime() !== dragged_event.end_date.getTime() ||
            dragged_event[self.resourceField] !== self.draggingEvent[self.resourceField]))) {
          toUpdate.push({ mode: mode, orig: self.draggingEvent, changed: dragged_event });
          
          if (typeof dragged_event.id === 'string') {
            self.updateMarkedTimespan(dragged_event.id.slice(dragged_event.id.length - 36), dragged_event.start_date.getTime(), dragged_event.end_date.getTime(), dragged_event.section_id);
          }
          
          if (self.draggingEvent) {
            if (self.span === 'year' ||
              self.span === 'month' ||
              self.span === 'week') {
              
              const start = self.getStartHour(section, dragged_event.start_date.getDay());
              const end = self.getEndHour(section, dragged_event.end_date.getDay());
              const startHour = Math.floor(start / 1000 / 60 / 60);
              const startMinute = start / 1000 / 60 % 60;
              const closeHour = Math.floor(end / 1000 / 60 / 60);
              const closeMinute = end / 1000 / 60 % 60;
              // correct the start date
              if (dragged_event.start_date.getHours() !== startHour && dragged_event.type !== 'vacation') {
                dragged_event.start_date.setHours(startHour, startMinute, 0);
              }
               
              // correct the end date
              if (dragged_event.end_date.getHours() !== closeHour && dragged_event.type !== 'vacation') {
                dragged_event.end_date.setHours(closeHour, closeMinute, 0);
              }
            }
            self.updateTaskDates(mode,
                                 dragged_event,
                                 self.draggingEvent.section_id);
          }
          dhtmlXTooltip.hide();
          self.schedulerInstance.setCurrentView();
        }
        
        for (const sid of self.selected) {
          const event = self.schedulerInstance.getEvent(sid);
          if (self.selectedEvents[sid] && (self.selectedEvents[sid].start_date.getTime() !== event.start_date.getTime() ||
            self.selectedEvents[sid].end_date.getTime() !== event.end_date.getTime() ||
            event[self.resourceField] !== self.selectedEvents[sid][self.resourceField])) {
            if (toUpdate.findIndex(u => u.orig.id === sid) === -1) {
              // its not in the list, add it
              toUpdate.push({ mode: mode, orig: self.selectedEvents[sid], changed: event });
              if (self.span === 'year' ||
                self.span === 'month' ||
                self.span === 'week') {
                
                const startHour = Math.floor(self.durationConversionOpts.startHour / 1000 / 60 / 60);
                const startMinute = self.durationConversionOpts.startHour / 1000 / 60 % 60;
                const closeHour = Math.floor(self.durationConversionOpts.closeHour / 1000 / 60 / 60);
                const closeMinute = self.durationConversionOpts.closeHour / 1000 / 60 % 60;
                // correct the start date
                if (event.start_date.getHours() !== startHour && event.type !== 'vacation') {
                  event.start_date.setHours(startHour, startMinute, 0);
                }
                 
                // correct the end date
                if (event.end_date.getHours() !== closeHour && event.type !== 'vacation') {
                  event.end_date.setHours(closeHour, closeMinute, 0);
                }
              }
              
              self.updateTaskDates(mode,
                                   event,
                                   self.selectedEvents[sid].section_id);
              
              self.updateMarkedTimespan(event.id.slice(event.id.length - 36), event.start_date.getTime(), event.end_date.getTime(), event.section_id);
            }
          }
        }
        
        if (toUpdate.length !== 0) {
          self.$emit('updated', toUpdate); 
        }
        self.draggingEvent = null;
        self.selectedEvents = {};
        usageCache = {}; // clear the cache so that the values are recalculated
        self.popupRemoveMenu();
        // setTimeout(() => {
        //   if (self.pendingRedrawn) {
        //     self.pendingRedrawn = false;
        //     self.$emit('redrawn');
        //   }
        // }, 500)
    });
    
    self.beforeEventChangedId = self.schedulerInstance.attachEvent("onBeforeEventChanged", function(ev) {
      if (self.escPressed) {
        self.escPressed = false;
        setTimeout(() => {
          // remove the listeners in case they are still present
          self.removeLinkClickListeners();
          self.addLinkClickListener();
        }, 1000);
        return false;
      }
      
      const section = self.schedulerInstance.getSection(ev.section_id);
      if (section && 
         ((section.type !== 'staff' &&
          section.type !== 'resource' &&
          section.type !== 'task' &&
          section.type !== 'activity' &&
          section.type !== 'booking') ||
          (section.type === 'staff' && self.resourceField === 'resourceUuid'))) {
        return false;   
      }       
      return true;
    });
    
    self.beforeLightboxId = self.schedulerInstance.attachEvent("onBeforeLightbox", function (){
        // show the editor dialog
        return false;
    });
    
    self.schedulerInstance.templates.tooltip_text = function(start, end, event) {
      const startDate = start.getFullYear() > 1970 ? `<p><span class="dhtmlXTooltipStartTime">${self.$t('task.field.startTime')}: ${formatTooltipDate(start, self.span === 'day' && event.type !== 'vacation')}</span></p>` : '';
      const endDate = end.getFullYear() < 3000 ? `<p><span class="dhtmlXTooltipCloseTime">${self.$t('task.field.closeTime')}: ${formatTooltipDate(end, self.span === 'day' && event.type !== 'vacation')}</span></p>` : '';
      let stageName = event.stageName ? `<p><span>${self.$t('task.field.stage')}: ${event.stageName}</span></p>` : '';
      let utilization = event.utilization ? `<p><span>${self.$t('staff.utilization')}: ${parseFloat(event.utilization * 100).toFixed(0)}%</span></p>` : '';
      const quantity = event.quantity ? `<p><span>${self.$t('units')} ${event.quantity}</span></p>` : '';
      let bookingWarn = self.filter.alertTaskBookings && event.type === 'task' && !event.booking ? `<p><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('task.error.booking')}</span></p>` : '';
      
      let booking_overlap = event.type === 'booking' && self.filter.alertBookings && event.type === 'booking' && event.hasOverlap ? `<p class="dhtmlXTooltipBookingWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('booking.error.booking_overlap')}</span></p>` : '';
      let task_overlap = event.type === 'task' && self.filter.alertTasks && event.hasOverlap ? `<p class="dhtmlXTooltipTaskWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('task.error.task_overlap')}</span></p>` : '';
      let activity_overlap = event.type === 'activity' && self.filter.alertActivities && event.hasOverlap ? `<p class="dhtmlXTooltipActivityWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('activity.error.activity_overlap')}</span></p>` : '';
      let nonwork_overlap = event.type === 'vacation' && self.filter.alertNonWork && event.hasOverlap ? `<p class="dhtmlXTooltipNonWorkWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('calendar.error.nonwork_overlap')}</span></p>` : '';
      let notes = event.noteList && event.noteList.length > 0 ? `<p><span class="dhtmlxNote" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}">${self.$t('note')}: ${event.noteList[event.noteList.length - 1].text}</span></p>` : `<p><span class="dhtmlxNote" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}"></span></p>`;
      let actualCostWarn = self.actualCostAlert(event) ? `<p class="dhtmlXTooltipActualCostWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.actualCostAlertMsg(event)}</span></p>` : '';
      let fixedDurationWarn = self.fixedDurationAlert(event) ? `<p class="dhtmlXTooltipFixedDurationWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.fixedDurationAlertMsg(event)}</span></p>` : '';
      let estimatedCost = typeof event.estimatedCost !== 'undefined' ? `<p><span class="dhtmlxEstimatedCost" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}">${self.$t('task.field.estimatedCost')}: ${costFormatAdv(event.estimatedCost, event.currency ? event.currency : 'USD')}</span></p>` : `<p><span class="dhtmlxEstimatedCost" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}"></span></p>`;
      let actualCost = typeof event.actualCost !== 'undefined' ? `<p><span class="dhtmlxActualCost" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}">${self.$t('task.field.actualCost')}: ${costFormatAdv(event.actualCost, event.currency ? event.currency : 'USD')}</span></p>` : `<p><span class="dhtmlxActualCost" data-id="${typeof event.id === 'string' ? event.id.slice(event.id.length - 36) : event.id}"></span></p>`;
      let not_employed = '';
      
      if (typeof event.id === 'string') {
        self.$emit('tooltip', event);
      }
      
      if (utilization === '' &&
          (event.type === 'task' ||
          event.type === 'booking' ||
          event.type === 'activity')) {
        const section = self.schedulerInstance.getSection(event.section_id);
        
        if (section && section[`${event.type}List`]) {
          const tasks = section[`${event.type}List`].filter(t => t.uuId === event.id.slice(event.id.length - 36));
          if (tasks.length === 1) {
            utilization = typeof tasks[0].utilization !== 'undefined' && tasks[0].utilization !== 1 ? `<p><span>${self.$t('staff.utilization')}: ${parseFloat(tasks[0].utilization * 100).toFixed(0)}%</span></p>` : '';
          }
        }   
        
        not_employed = (event.end_date.getTime() > (section.until + 86400000) || event.start_date.getTime() < section.begin) ? `<p class="dhtmlXTooltipNotEmployedWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${self.$t('task.error.not_employed')}</span></p>` : '';
      }
      
      if (typeof event.tp !== 'undefined') {
        const projectName = self.projectId ? '' : `${event.pn} / `;
        return `<div class="header-box">
            <div class="tag ${self.getTagClass(event)}">${self.getTagLabel(event)}</div>
            <div class="header">${projectName}${event.text}</div>
          </div>
          <div class="task-tooltip-body">
            ${startDate}
            ${endDate}
            <p><span class="dhtmlXTooltipDuration">${self.taskEstimatedDuration(event.te)}</span></p>
            <p><span>${self.$t('project.field.progress')}: ${parseFloat(event.tp * 100).toFixed(0)}%</span></p>
            ${utilization}
            ${stageName}
            ${nonwork_overlap}      
            ${not_employed}     
            ${estimatedCost}
            ${actualCost}
            ${notes}
            ${bookingWarn}
            ${booking_overlap}
            ${task_overlap}
            ${activity_overlap}
            ${actualCostWarn}
            ${fixedDurationWarn} 
          </div>`;
      }
      
      if (typeof event.te !== 'undefined') {
        return `<div class="header-box">
              <div class="tag ${self.getTagClass(event)}">${self.getTagLabel(event)}</div>
              <div class="header">${event.text}</div>
            </div>
            <div class="task-tooltip-body">
              ${startDate}
              ${endDate}
              <p><span class="dhtmlXTooltipDuration">${self.taskEstimatedDuration(event.te)}</span></p>
              ${utilization}
              ${stageName}        
              ${estimatedCost}
              ${actualCost}
              ${notes}
              ${bookingWarn}
              ${booking_overlap}
              ${task_overlap}
              ${activity_overlap}
              ${nonwork_overlap} 
              ${not_employed}
              ${actualCostWarn}
              ${fixedDurationWarn}   
            </div>`;
      }
      
      if (end.getFullYear() >= 3000) {
        return `<div class="header-box">
              <div class="tag ${self.getTagClass(event)}">${self.getTagLabel(event)}</div>
              <div class="header">${event.text}</div>
            </div>
            <div class="task-tooltip-body">
              <p><span class="dhtmlXTooltipStartTime">${self.$t('task.field.startTime')}: ${startDate}</span></p>
              ${utilization}
              ${quantity}
              ${stageName}           
              ${estimatedCost}
              ${actualCost}
              ${notes}
              ${bookingWarn}
              ${booking_overlap}
              ${task_overlap}
              ${activity_overlap}
              ${nonwork_overlap}
              ${not_employed}
              ${actualCostWarn}
              ${fixedDurationWarn} 
            </div>`;
      }
      
      return `<div class="header-box">
            <div class="tag ${self.getTagClass(event)}">${self.getTagLabel(event)}</div>
            <div class="header">${event.text}</div>
          </div>
          <div class="task-tooltip-body">
            ${startDate}
            ${endDate}
            ${utilization}
            ${quantity}
            ${stageName}           
            ${estimatedCost}
            ${actualCost}
            ${notes}
            ${bookingWarn}
            ${booking_overlap}
            ${task_overlap}
            ${activity_overlap}
            ${nonwork_overlap}
            ${not_employed}
            ${actualCostWarn}
            ${fixedDurationWarn} 
          </div>`;
    };
    
    self.eventAddedId = self.schedulerInstance.attachEvent("onEventAdded", function(id){
        const event = self.schedulerInstance.getEvent(id); 
        event.id = `${event.id}`;
    });
    
    self.onScrollId = self.schedulerInstance.getView().attachEvent("onScroll", function(left, top){
      self.$emit('schedulerScroll', { left: left, top: top });
    });
    
    self.schedulerInstance._click.buttons["delete"] = function() { 
      if (self.disableDelete) {
        return;
      }
      self.$emit('deleteEvent');
    };
    
    self.dblClickId = self.schedulerInstance.attachEvent("onDblClick", function (id){
      clearTimeout(self.sin_timer);
      self.sin_timer = null;
      const event = self.schedulerInstance.getEvent(id);
      setTimeout(() => {
        dhtmlXTooltip.hide();
      }, 100);
      self.$emit('clickItem', {
        id: id.slice(id.length - 36),
        data: {  
          name: event.text,
          type: event.type, 
          pu: event.pu,
          startHour: event.startHour,
          endHour: event.endHour,
          calendar_type: event.calendar_type,
          start_date: event.start_date,
          end_date: event.end_date,
          readonly: event.readonly,
          quantity: event.quantity,
          utilization: event.utilization,
          staffUuid: event.staffUuid
        } 
      });
    });
    
    self.clickId = self.schedulerInstance.attachEvent("onClick", function (id, ev){
      self.clickId = id;
      self.eventObj = ev;
      if (self.sin_timer == null) {
        self.sin_timer = setTimeout(self.onEventClick, 250 );
      }
      return true;
    });
    
    self.emptyClickId = self.schedulerInstance.attachEvent("onEmptyClick", function (){
      const toUpdate = self.selected;
      self.selected = [];
      self.$emit('selectionChanged', []);
      
      if (toUpdate.length !== 0) {
        // save the focus for restoration
        const focus = document.querySelector('.dhx_focus_slot');
        const container = focus !== null ? focus.parentElement : null;
        
        for (const id of toUpdate) {
          self.schedulerInstance.updateEvent(id);
        }
        
        if (focus) {
          container.appendChild(focus);
          focus.focus();
        }
      }
    });
    
    this.eventCreatedId = self.schedulerInstance.attachEvent("onEventCreated", function(id,e){
      const event = self.schedulerInstance.getEvent(id);
      const section = self.schedulerInstance.getSection(event.section_id);
      if (self.schedulerInstance.config.dblclick_create) {
        self.schedulerInstance.config.dblclick_create = false;
        setTimeout(() => {
          self.schedulerInstance.config.dblclick_create = true;
        }, 2000);
      }
        
      if (section &&
          ((section.type !== 'staff' && section.type !== 'resource') ||
           (section.type === 'staff' && self.resourceField === 'resourceUuid'))) {
        self.draggingEvent = null;
        return false;
      }
      
      self.draggingEvent = cloneDeep(event); 
      self.draggingEvent.te = 0;
      self.draggingEvent.type = 'new';
      if (self.span === 'year' ||
          self.span === 'month') {
        // correct the end date
        event.end_date.setDate(event.end_date.getDate() - 1);
      }
      else if (self.span === 'week') {
        // correct the end date
        event.end_date.setDate(event.end_date.getDate() - 3);
      }
      else if (self.span === 'day') {
        // correct the times
        const start = self.getStartHour(section, event.start_date.getDay());
        const end = self.getEndHour(section, event.end_date.getDay());
        const startHour = Math.floor(start / 1000 / 60 / 60);
        const startMinute = start / 1000 / 60 % 60;
        const closeHour = Math.floor(end / 1000 / 60 / 60);
        const closeMinute = end / 1000 / 60 % 60;
        event.start_date.setHours(startHour, startMinute, 0);
        event.end_date.setHours(closeHour, closeMinute, 0);
        event.end_date.setDate(event.start_date.getDate());
      }
      dhtmlXTooltip.show(e, self.schedulerInstance.templates.tooltip_text(self.draggingEvent.start_date, self.draggingEvent.end_date, self.draggingEvent));
    });
    
    self.beforeDragId = self.schedulerInstance.attachEvent("onBeforeDrag", function (id, mode, e){
      mode; // prevent unused;
      self.escPressed = false;
      
      if (self.lockPlanner) {
        // when locked events cannot be modified
        return false;
      }
      
      const event = id !== null ? self.schedulerInstance.getEvent(id) : null;
      
      if (!self.selected.includes(id) && !e.ctrlKey && !e.metaKey) {
        let isNotEmpty = self.selected.length != 0;
        // remove all the selected and select this event
        const removed = id !== null ? self.selected.splice(0, self.selected.length, id) : self.selected.splice(0, self.selected.length);
        if (isNotEmpty || id != null) {
          self.$emit('selectionChanged', self.selected);
        }
        for (const remove of removed) {
          self.schedulerInstance.updateEvent(remove);
        }
      }
      
      if (mode === 'create' &&
          !self.showAdd) {
        // permissions prevent the user from creating events
        return false;   
      }
      
      if (event && event.readonly) {
        // read only events cannot be modified
        return false;
      }
      
      for (const sid of self.selected) {
        const event = self.schedulerInstance.getEvent(sid); 
        self.selectedEvents[sid] = cloneDeep(event);
      }
      
      // prevent drag to create on non-staff elements
      const section_id = self.getSectionId(e.target.parentNode);
      if (section_id) {
        
        const section = self.schedulerInstance.getSection(section_id);
        
        if (section && mode === 'create' &&
            ((section.type !== 'staff' && section.type !== 'resource') ||
             (section.type === 'staff' && self.resourceField === 'resourceUuid'))) {
          self.draggingEvent = null;
          return false;
        }
      }
      self.draggingEvent = cloneDeep(event);
      
      return true;
    });

    self.afterFolderToggleId = self.schedulerInstance.attachEvent("onAfterFolderToggle", function(section, isOpen){
      if (section !== null) {
        self.$emit('folderToggle', { section: section, isOpen: isOpen });
        self.addLinkClickListener();
      }
    });
    
    self.eventDragId = self.schedulerInstance.attachEvent("onEventDrag", function (id, mode, ev){
      const toUpdate = self.selected;
      const draggedEvent = self.schedulerInstance.getEvent(id);
      if (draggedEvent &&
          self.draggingEvent) {
        
        if (self.escPressed) {
          draggedEvent.start_date = new Date(self.draggingEvent.start_date);
          draggedEvent.end_date = new Date(self.draggingEvent.end_date);
          self.schedulerInstance.updateEvent(id);
        }
        
        if (mode === 'move') {
          if (ev.ctrlKey) {
            draggedEvent.end_date = self.draggingEvent.end_date;
            draggedEvent.start_date = self.draggingEvent.start_date;
            self.schedulerInstance.updateEvent(id);
          }
          else if (!ev.shiftKey) {
            if (self.span === 'week') {
              self.schedulerInstance.config.time_step = 10080;
            }
            else if (self.span === 'month') {
              self.schedulerInstance.config.time_step = 10080 * 4;
              const diff = self.draggingEvent.end_date.getTime() - self.draggingEvent.start_date.getTime();
              
              const endMonthDate = new Date(draggedEvent.start_date.getFullYear(), draggedEvent.start_date.getMonth() + 1, 0).getDate();
              if (draggedEvent.type !== 'vacation' &&
                  (draggedEvent.start_date.getDate() !== 1)) {
                draggedEvent.start_date.setDate(1);
                const endDate = new Date(draggedEvent.start_date.getTime() + diff);
                
                draggedEvent.end_date = endDate;
                self.schedulerInstance.updateEvent(id);
              }
            }
            else {

              const section = self.schedulerInstance.getSection(draggedEvent.section_id);
              const calendars = section.calendars;
              if (typeof calendars !== 'undefined') {
                const result = getStartAndEndTime(draggedEvent, calendars, self);
                if (self.span === 'day' &&
                    draggedEvent.type !== 'vacation' &&
                    (draggedEvent.start_date.getHours() !== result.startHour ||
                    draggedEvent.start_date.getMinutes() !== result.startMinutes ||
                    draggedEvent.end_date.getHours() !== result.endHour ||
                    draggedEvent.end_date.getMinutes() !== result.endMinutes)) {
                  // get the difference of the original event
                  const diffday = self.draggingEvent.end_date.getTime() - self.draggingEvent.start_date.getTime();
                  draggedEvent.start_date.setHours(result.startHour, result.startMinutes, 0);
                  draggedEvent.end_date = new Date(draggedEvent.start_date.getTime() + diffday);
                  self.schedulerInstance.updateEvent(id);
                }
              }
            }  
            
            if (draggedEvent.type === 'vacation' &&
                  (draggedEvent.start_date.getHours() !== self.getFirstHour() ||
                  draggedEvent.end_date.getHours() !== self.getLastHour())) {
                // get the difference
                const diff = draggedEvent.end_date.getTime() - draggedEvent.start_date.getTime();
                draggedEvent.start_date.setHours(self.getFirstHour(), 0, 0);
                draggedEvent.end_date = new Date(draggedEvent.start_date.getTime() + diff);
                scheduler.updateEvent(id);
            }
          }
          else if (self.schedulerInstance.config.time_step !== 15) {
            self.schedulerInstance.config.time_step = 15;
          }
        }
        else if (self.schedulerInstance.config.time_step !== 15) {
          self.schedulerInstance.config.time_step = 15;
        }
        
        if (!ev.shiftKey && draggedEvent.type !== 'vacation' && mode !== 'move') {
          // mode is not 'move'
          if (self.span === 'day') {
            const section = self.schedulerInstance.getSection(draggedEvent.section_id);
            const calendars = section.calendars;
            const result = getStartAndEndTime(draggedEvent, calendars, self);
            
            if (draggedEvent.end_date.getTime() !== self.draggingEvent.end_date.getTime() ||
                (draggedEvent.end_date.getHours() !== result.endHour || draggedEvent.end_date.getMinutes() !== result.endMinutes)) {
              draggedEvent.end_date.setHours(result.endHour, result.endMinutes, 0);
              self.schedulerInstance.updateEvent(id);
            }
            
            if (draggedEvent.start_date.getTime() !== self.draggingEvent.start_date.getTime() ||
                (draggedEvent.start_date.getHours() !== result.startHour || draggedEvent.start_date.getMinutes() !== result.startMinutes)) {
              draggedEvent.start_date.setHours(result.startHour, result.startMinutes, 0);
              self.schedulerInstance.updateEvent(id);
            }
          }
          else if (self.span === 'week') {
            if (draggedEvent.end_date.getTime() !== self.draggingEvent.end_date.getTime() ||
                draggedEvent.end_date.getDay() !== 5) { // check to see if this is already Friday
              draggedEvent.end_date = new Date(moment(draggedEvent.end_date).startOf('isoWeek').add(4, 'days').format('LLL'));
                    
              const closeHour = Math.floor(self.durationConversionOpts.closeHour / 1000 / 60 / 60);
              const closeMinute = self.durationConversionOpts.closeHour / 1000 / 60 % 60;
              draggedEvent.end_date.setHours(closeHour, closeMinute, 0);
              self.schedulerInstance.updateEvent(id);
            }
            
            if (draggedEvent.start_date.getTime() !== self.draggingEvent.start_date.getTime() ||
                draggedEvent.start_date.getDay() !== 1) { // check to see if this is already Monday
              const startHour = Math.floor(self.durationConversionOpts.startHour / 1000 / 60 / 60);
              const startMinute = self.durationConversionOpts.startHour / 1000 / 60 % 60;
              draggedEvent.start_date = moment(draggedEvent.start_date).startOf('isoweek').add(startHour, 'hours').add(startMinute, 'minutes').toDate();
              self.schedulerInstance.updateEvent(id);
            }
          }
          else if (self.span === 'month') {
            const endMonthDate = new Date(draggedEvent.end_date.getFullYear(), draggedEvent.end_date.getMonth() + 1, 0).getDate();
            if (draggedEvent.end_date.getTime() !== self.draggingEvent.end_date.getTime() ||
            draggedEvent.end_date.getDate() !== endMonthDate) {
              draggedEvent.end_date = new Date(draggedEvent.end_date.getFullYear(), draggedEvent.end_date.getMonth() + 1, 0);
              self.schedulerInstance.updateEvent(id);
            }
            
            if (draggedEvent.start_date.getTime() !== self.draggingEvent.start_date.getTime() ||
                draggedEvent.start_date.getDate() !== 1) {
              draggedEvent.start_date = new Date(moment(draggedEvent.start_date).startOf('month').format('LLL'));
              self.schedulerInstance.updateEvent(id);
            }
          }
        }
        
        const diffEnd = draggedEvent.end_date - self.draggingEvent.end_date; 
        const diffStart = draggedEvent.start_date - self.draggingEvent.start_date;
        for (const sid of toUpdate) {
          if (sid !== id) {
            const selected_event = self.selectedEvents[sid]; 
            const resized_event = self.schedulerInstance.getEvent(sid);
            if (selected_event) {
              if (self.draggingEvent.section_id !== draggedEvent.section_id) {
                // Event has been dragged into a different section
                resized_event.section_id = draggedEvent.section_id;
                resized_event[self.resourceField] = resized_event.section_id.slice(resized_event.section_id.length - 36);
              }
              else {
                resized_event.section_id = selected_event.section_id;
                resized_event[self.resourceField] = selected_event.section_id.slice(selected_event.section_id.length - 36);
              }
              resized_event.end_date = new Date(selected_event.end_date.getTime() + diffEnd);
              resized_event.start_date = new Date(selected_event.start_date.getTime() + diffStart);
              self.schedulerInstance.updateEvent(sid);
            }
          }
        }
           
        let start_date = new Date(draggedEvent.start_date);
        let end_date = new Date(draggedEvent.end_date);
        
        
        
        // get the tooltip and update the values
        const elStartTime = document.querySelector(".dhtmlXTooltipStartTime");
        if (elStartTime) {
          const startDate = formatTooltipDate(start_date, self.span === 'day' && draggedEvent.type !== 'vacation');
          elStartTime.innerHTML = `${self.$t('task.field.startTime')}: ${startDate}`;
        }
        const elCloseTime = document.querySelector(".dhtmlXTooltipCloseTime");
        if (elCloseTime) {
          const endDate = formatTooltipDate(end_date, self.span === 'day' && draggedEvent.type !== 'vacation');
          elCloseTime.innerHTML = `${self.$t('task.field.closeTime')}: ${endDate}`;
        }
        
        self.checkTooltipWarnings();
        
        if (mode !== 'move') {
          const elDuration = document.querySelector(".dhtmlXTooltipDuration");
          if (elDuration && !draggedEvent.lockDuration) {
            const section = self.schedulerInstance.getSection(draggedEvent.section_id);
            let calendar = null;
            if (section.calendars) {
              const cals = [];
              const names = [ 'staff', 'location', 'base_calendar'];
              
              for (let i = section.calendars.length - 1; i >= 0; i--) {
                const c = section.calendars[i];
                cals.push({ name: names[i], calendarList: c });
              }
              calendar = transformCalendar(processCalendar(cals));
            }
            else {
              calendar = DEFAULT_CALENDAR;
            }
            
            const debounceDuration = debounce(() => {
              elDuration.innerHTML = `${self.$t('task.field.estimatedDuration')}: ${recalcDuration(start_date, end_date, calendar, self.span, self.staffAlloc, mode, null, draggedEvent.lockDuration, self.durationConversionOpts).value}`;
            }, 100);
            debounceDuration();
          }
        }
      }
    });
    
    self.viewChangeId = self.schedulerInstance.attachEvent("onViewChange", function (new_mode , new_date){
      if (new_date && !self.settingView) { // ignore the view change if we are setting the date
        self.$emit('dateChange', { date: new_date, initialized: self.initialized });
      }
      
      self.$nextTick(() => {
        self.addLinkClickListener();
        self.addWheelListener();
      });
    });
    
    self.schedulerInstance.addShortcut("ctrl+c,meta+c", function(){ 
      if (!self.disableCopy &&
          self.selected.length !== 0) {
        self.eventCopy();
      }
    },"event");
    
    /* eslint-enable no-undef */
    
    this.$nextTick(() => {
      self.addLinkClickListener();
      self.addWheelListener();
    });
  },
  created() {
    this.getDurationConversionOpts();
    this.schedulerInstance = Scheduler.getSchedulerInstance(); // eslint-disable-line no-undef
    this.$emit('schedulerCreated', this);
    this.$emit('markedSpansCreated', this.markedSpans);
    EventBus.$on('toggle-sidebar', () => {
      /* eslint-disable no-undef */
      if (this.initialized) {
        setTimeout(() => {
          this.schedulerInstance.setCurrentView();
        }, 300);
      }
      /* eslint-enable no-undef */
    });
  },
  beforeDestroy() {
    this.cleanup();
    EventBus.$off('toggle-sidebar');
    
    if (this.schedulerInstance) {
      /* eslint-disable no-undef */
      this.schedulerInstance.clearAll();
      this.schedulerInstance.detachEvent(this.beforeDragId);
      this.schedulerInstance.detachEvent(this.eventDragId);
      this.schedulerInstance.detachEvent(this.dragEndId);
      this.schedulerInstance.detachEvent(this.eventCreatedId);
      this.schedulerInstance.detachEvent(this.beforeEventChangedId); 
      this.schedulerInstance.detachEvent(this.beforeLightboxId);
      this.schedulerInstance.detachEvent(this.eventAddedId);
      this.schedulerInstance.detachEvent(this.onScrollId);
      this.schedulerInstance.detachEvent(this.dblClickId);
      this.schedulerInstance.detachEvent(this.clickId);
      this.schedulerInstance.detachEvent(this.emptyClickId);
      this.schedulerInstance.detachEvent(this.afterFolderToggleId);
      this.schedulerInstance.detachEvent(this.viewChangeId);
      this.schedulerInstance.detachEvent(this.dataRenderId);
      this.schedulerInstance.config.key_nav = false;
      /* eslint-enable no-undef */
      this.schedulerInstance = null;
    }
    
  },
  methods: {
    checkTooltipWarnings() {
      if (this.bookingevs) {
        let booking_overlap = this.filter.alertBookings && this.bookingevs ? `<p class="dhtmlXTooltipBookingWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${this.$t('booking.error.booking_overlap')}</span></p>` : '';
        const dhtmlXTooltipBookingWarn = document.querySelector(".dhtmlXTooltipBookingWarn");
        if (!dhtmlXTooltipBookingWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.innerHTML += booking_overlap;
          }
        }          
      }
      else {
        // remove the warning
        const dhtmlXTooltipBookingWarn = document.querySelector(".dhtmlXTooltipBookingWarn");
        if (dhtmlXTooltipBookingWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.removeChild(dhtmlXTooltipBookingWarn);
          }
        }  
      }
      
      if (this.activityevs) {
        let activity_overlap = this.filter.alertActivities && this.activityevs ? `<p class="dhtmlXTooltipActivityWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${this.$t('activity.error.activity_overlap')}</span></p>` : '';
        const dhtmlXTooltipActivityWarn = document.querySelector(".dhtmlXTooltipActivityWarn");
        if (!dhtmlXTooltipActivityWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.innerHTML += activity_overlap;
          }
        }          
      }
      else {
        // remove the warning
        const dhtmlXTooltipActivityWarn = document.querySelector(".dhtmlXTooltipActivityWarn");
        if (dhtmlXTooltipActivityWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.removeChild(dhtmlXTooltipActivityWarn);
          }
        }  
      }
      
      if (this.taskevs) {
        let task_overlap = this.filter.alertTasks && this.taskevs ? `<p class="dhtmlXTooltipActivityWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${this.$t('task.error.task_overlap')}</span></p>` : '';
        const dhtmlXTooltipTaskWarn = document.querySelector(".dhtmlXTooltipTaskWarn");
        if (!dhtmlXTooltipTaskWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.innerHTML += task_overlap;
          }
        }          
      }
      else {
        // remove the warning
        const dhtmlXTooltipTaskWarn = document.querySelector(".dhtmlXTooltipTaskWarn");
        if (dhtmlXTooltipTaskWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.removeChild(dhtmlXTooltipTaskWarn);
          }
        }  
      }
      
      
      if (this.nonworkevs) {
        let nonwork_overlap = this.filter.alertNonWork && this.nonworkevs.length > 0 ? `<p class="dhtmlXTooltipNonWorkWarn"><span><i class='readonly-icon fas fa-triangle-exclamation'></i>${this.$t('calendar.error.nonwork_overlap')}</span></p>` : '';
        const dhtmlXTooltipNonWorkWarn = document.querySelector(".dhtmlXTooltipNonWorkWarn");
        if (!dhtmlXTooltipNonWorkWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.innerHTML += nonwork_overlap;
          }
        }          
      }
      else {
        // remove the warning
        const dhtmlXTooltipNonWorkWarn = document.querySelector(".dhtmlXTooltipNonWorkWarn");
        if (dhtmlXTooltipNonWorkWarn) {
          const elPopup = document.querySelector(".task-tooltip-body");
          if (elPopup) {
            elPopup.removeChild(dhtmlXTooltipNonWorkWarn);
          }
        }  
      }
    },
    isModal(e) {
      let ret = false;
      let elem = e.target;
      while (elem) {
        if (elem.className.includes('modal-body') ||
            elem.className.includes('modal')) {
          return true;
        }
        elem = elem.parentElement;
      }
      return ret;
    },
    keyDown(e){
      const self = this; 
      e = e || window.event;
      var key = e.which || e.keyCode; // keyCode detection
      var ctrl = e.ctrlKey ? e.ctrlKey : ((key === 17) ? true : false); // ctrl detection
  
      if ( key == 86 && (ctrl || e.metaKey) ) { // ctrl+v  
        if (self.copied && !self.isModal(e)) {
          self.eventPaste();
          e.stopPropagation();
          e.preventDefault();
        }
      }
      else if (key === 27) {
        self.escPressed = true;
        /* eslint-disable no-undef */
      
        for (const sid of self.selected) {
          const slEvent = self.selectedEvents[sid];
          const event = self.schedulerInstance.getEvent(sid);
          if (slEvent && event) {
            event.start_date = new Date(slEvent.start_date);
            event.end_date = new Date(slEvent.end_date);
            event.section_id = slEvent.section_id;
            self.schedulerInstance.updateEvent(sid);
          }
        }
        self.schedulerInstance._on_mouse_up(e);
        self.draggingEvent = null;
        dhtmlXTooltip.hide();
        /* eslint-enable no-undef */
      }
      else if (key === 9) { // tab
        if (self.selected.length === 1 &&
            e.target.className.includes('dhx_cal_event_line')) {
          const event_id = self.selected[0];
          const el = document.querySelector(`[event_id="${event_id}"]`);
          if (el) {
            self.findNextEvent(event_id, el.parentElement, e);
          }
          else {
            self.selectEvent(null, event_id);
          }
          e.stopPropagation();
          e.preventDefault();
        }
      }
      else if (key === 13 &&
               e.target.tagName !== 'INPUT' && !self.isModal(e)) { // enter
        if (self.selected.length === 1) {
          const event_id = self.selected[0];
          /* eslint-disable no-undef */
          const section = self.schedulerInstance.getEvent(event_id);
          self.$emit('clickItem', {id: event_id.slice(event_id.length - 36), data: section });
          setTimeout(() => {
            dhtmlXTooltip.hide();
          }, 100);
          /* eslint-enable no-undef */
        }
      }
    },
    findNextEvent(event_id, parentElement, e, found = false) {
      let set = false;
      
      // sort them by left offset
      let parentChildren = [...parentElement.children].sort((a, b) => {
        if (e.shiftKey) {
          if (a.offsetLeft === b.offsetLeft) {
            return a.offsetTop > b.offsetTop ? -1 : 1;
          }
          return a.offsetLeft > b.offsetLeft ? -1 : 1;
        }
        if (a.offsetLeft === b.offsetLeft) {
          return a.offsetTop < b.offsetTop ? -1 : 1;
        }
        return a.offsetLeft < b.offsetLeft ? -1 : 1;
      });
      
      for (let idx = 0; idx < parentChildren.length; idx++) {
        let child = parentChildren[idx];
        if (child.classList.contains('dhx_cal_event_line')) {
          if (found) {
            // select the next child
            this.selectEvent(child, child.getAttribute('event_id'));
            set = true;
            break;
          }
          
          let child_id = child.getAttribute('event_id');
          if (child_id === event_id) {
            found = true;
          }
        }
      }
      
      const next = e.shiftKey ? parentElement.previousSibling : parentElement.nextSibling;
      if (!set && next) {
        // continue with next row
        this.findNextEvent(event_id, next, e, found);
      }
      else if (!set && !next) {
        /* eslint-disable no-undef */
        const events = this.schedulerInstance.getEvents();
        /* eslint-enable no-undef */
        if (events && events.length > 0) {
          events.sort((a, b) => {
            if (a.start_date.getTime() === b.start_date.getTime()) {
              // sort by name              
              /* eslint-disable no-undef */
              const section_a = this.schedulerInstance.getSection(a.section_id);
              const section_b = this.schedulerInstance.getSection(b.section_id);
              /* eslint-enable no-undef */
              if (section_a &&
                  section_b) {
                return ((section_a.name < section_b.name) ? -1 : ((section_a.name > section_b.name) ? 1 : 0));
              }
            }
            return a.start_date.getTime() - b.start_date.getTime();
          });
          
          const index = events.findIndex(e => e.id === event_id);
          if (index !== -1) {
            if (e.shiftKey && index - 1 > 0) {
              this.selectEvent(null, events[index - 1].id);
            }
            else if (!e.shiftKey && index + 1 < events.length) {
              this.selectEvent(null, events[index + 1].id);
            }
          }
        }
      }
    },
    isVisible(ele, container) {
      if (!ele) {
        return false;
      }
      const eleTop = ele.parentElement.offsetTop;
      const eleLeft = ele.offsetLeft;
      const eleBottom = eleTop + ele.clientHeight;
      const eleRight = eleLeft + ele.clientWidth;
      
      const containerTop = container.scrollTop;
      const containerLeft = container.scrollLeft;
      const containerBottom = containerTop + container.clientHeight;
      const containerRight = containerLeft + (container.clientWidth - 278); // 278 the tree width
      
      // The element is fully visible in the container
      return (
          (eleTop >= containerTop && eleBottom <= containerBottom &&
          ((eleLeft >= containerLeft + 50 && eleLeft < containerRight - 50) ||
          (eleRight >= containerLeft + 50 && eleRight < containerRight - 50))) ||
          // Some part of the element is visible in the container
          (eleTop < containerTop && containerTop < eleBottom)
      );
    },
    selectEvent(child, child_id, showing = false) {
      const old = this.selected.splice(0, this.selected.length, child_id);
      const self = this;
      const el = document.querySelector(`[event_id="${child_id}"]`);
      const left = el ? el.offsetLeft : 0;
      const top = el ? el.parentElement.offsetTop : 0;
      const scrollable = document.querySelector('.dhx_timeline_scrollable_data');
      const isVisible = this.isVisible(el, scrollable);
      
      /* eslint-disable no-undef */
      this.schedulerInstance.updateEvent(old[0]);
      this.schedulerInstance.updateEvent(child_id);
      /* eslint-enable no-undef */

      if (!el && !showing) {
        self.scrollToSection(child_id);
      }
      else {
        if (scrollable && !isVisible) {
          scrollable.scrollTo(left, top);
          scrollable.focus();
        }
      }
    },
    scrollToSection(id) {
      /* eslint-disable no-undef */
      const timeline = this.schedulerInstance.getView();
      const event = this.schedulerInstance.getEvent(id);
      /* eslint-enable no-undef */
      var top = timeline.posFromSection(event.section_id);
      timeline.scrollTo({date: event.start_date, top: top});
    },
    taskEstimatedDuration(te) {
      if (this.staffAlloc === 'hours') {
        if (te < 1) {
          return `${this.$t('task.field.estimatedDuration')}: ${toFixed(te, 1)}h`;
        }
        return `${this.$t('task.field.estimatedDuration')}: ${toFixed(te, 0)}h`;
      }
      else {
        return `${this.$t('task.field.estimatedDuration')}: ${parseFloat(te / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D`;
      }
    },
    cleanup() {
      // Remove click listeners attached to tree labels
      this.removeLinkClickListeners();
      
      document.removeEventListener("keydown", this.keyDown);
      
      /* eslint-disable no-undef */
      dhtmlXTooltip.hide();
      /* eslint-enable no-undef */
    },
    onEventClick() {
      let changed = false;
      this.sin_timer = null;
      const id = this.clickId;
      const ev = this.eventObj;
      /* eslint-disable no-undef */
      const readonly = this.schedulerInstance.getEvent(id).readonly || this.lockPlanner;
      /* eslint-enable no-undef */
      if (ev.ctrlKey || ev.metaKey) {
        const index = this.selected.findIndex(i => i === id);
        if (index === -1) {
          if (!readonly) {
            this.selected.push(id);
          }
        }
        else {
          this.selected.splice(index, 1);
        }
        changed = true;
      }
      else if ((!this.selected.includes(id) || this.selected.length > 1) && !readonly) {
        const removed = this.selected.splice(0, this.selected.length, id);
        for (const remove of removed) {
          /* eslint-disable no-undef */
          this.schedulerInstance.updateEvent(remove);
          /* eslint-enable no-undef */
        }
        changed = true;
      }
      
      if (changed) {
        this.$emit('selectionChanged', this.selected);
        /* eslint-disable no-undef */
        this.schedulerInstance.updateEvent(id);
        /* eslint-enable no-undef */
      }
      this.eventObj = null;
    },
    updateData() {
      // clear out the tasks
      tasksMap = {};
      
      const scrolling = document.querySelector('.dhx_timeline_scrollable_data');
      const scrollTop = scrolling ? scrolling.scrollTop : 0;
      const scrollLeft = scrolling ? scrolling.scrollLeft : 0;
      
      // We are recreating the scheduler, cleanup any click listeners
      this.cleanup();
      
      const self = this;
      /* eslint-disable no-undef */
      this.schedulerInstance.clearAll();
      this.schedulerInstance.xy.scale_height=this.span == 'year'? 40 : 20;
      this.schedulerInstance.serverList("sections", this.treeData);
      const x_size = this.getSize();
      
      this.schedulerInstance.config.event_yoff = this.filter.usage ? 26 : 0;
      this.schedulerInstance.locale.date = {
        month_full:[
          moment().month(0).format('MMMM'), 
          moment().month(1).format('MMMM'), 
          moment().month(2).format('MMMM'), 
          moment().month(3).format('MMMM'), 
          moment().month(4).format('MMMM'), 
          moment().month(5).format('MMMM'), 
          moment().month(6).format('MMMM'), 
          moment().month(7).format('MMMM'), 
          moment().month(8).format('MMMM'), 
          moment().month(9).format('MMMM'), 
          moment().month(10).format('MMMM'), 
          moment().month(11).format('MMMM')
        ],
        month_short:[
          moment().month(0).format('MMM'), 
          moment().month(1).format('MMM'), 
          moment().month(2).format('MMM'), 
          moment().month(3).format('MMM'), 
          moment().month(4).format('MMM'), 
          moment().month(5).format('MMM'), 
          moment().month(6).format('MMM'), 
          moment().month(7).format('MMM'), 
          moment().month(8).format('MMM'), 
          moment().month(9).format('MMM'), 
          moment().month(10).format('MMM'), 
          moment().month(11).format('MMM')
        ],
        day_full:[
          moment().day(0).format('dddd'), 
          moment().day(1).format('dddd'), 
          moment().day(2).format('dddd'), 
          moment().day(3).format('dddd'), 
          moment().day(4).format('dddd'), 
          moment().day(5).format('dddd'), 
          moment().day(6).format('dddd')
        ],
        day_short:[
          moment().day(0).format('ddd'), 
          moment().day(1).format('ddd'), 
          moment().day(2).format('ddd'), 
          moment().day(3).format('ddd'), 
          moment().day(4).format('ddd'), 
          moment().day(5).format('ddd'), 
          moment().day(6).format('ddd')
        ],
      }
      

      var getOrder = function(type) {
        if (type === 'booking') {
          return 0;
        }
        else if (type === 'task') {
          return 1;
        }
        else if (type === 'activity') {
          return 2;
        }
        return 3;
      };
      var prioritySort = function(a, b) {
        const aOrder = getOrder(a.type);
        const bOrder = getOrder(b.type);
        if(aOrder === bOrder)
          return a.id<b.id?1:-1;
        return aOrder > bOrder ? 1 : -1;
      };

      // this function is not universal and should be changed depending on your timeline configuration
      var timeframeSort = function(a, b) {
        var a_timeframe_start = self.schedulerInstance.date.date_part(new Date(a.start_date));
        var a_timeframe_end = self.schedulerInstance.date.date_part(new Date(a.end_date));
        if (+a.end_date != +a_timeframe_end) {
          a_timeframe_end = self.schedulerInstance.date.add(a_timeframe_end, 1, "day");
        }

        var b_timeframe_start = self.schedulerInstance.date.date_part(new Date(b.start_date));

        if (a_timeframe_start < b.end_date && a_timeframe_end > b.start_date && +a_timeframe_start == +b_timeframe_start) {
          return prioritySort(a, b);
        } else {
          return (a_timeframe_start < b_timeframe_start) ? -1 : 1;
        }
      };

      const config = {
        section_autoheight: false,
        name: "timeline",
        x_unit: this.span,
        x_date: this.getDateLabel(),
        x_step: 1,
        x_size: x_size,
        x_start: 0,
        x_length: 1,
        y_unit: this.schedulerInstance.serverList("sections"),
        y_property: "section_id",
        render: "tree",
        folder_dy:28,
        dy: this.filter.usage ? 53 : 28,
        dx: 0,
        folder_events_available: true,
        scrollable: true,
        cell_template: true,
        sort: timeframeSort
      };
      
      if (this.filter.work_hours && this.filter.hideNonWorking) {
        config.first_hour = this.filter.work_hours.first_hour;
        config.last_hour = this.filter.work_hours.last_hour;
      }
      
      if (this.span === 'month') {
        config.second_scale = {
            x_unit: "year", // unit which should be used for second scale
            x_date: "%Y" // date format which should be used for second scale, "July 01"
        };
      }
      else if (this.span === 'week') {
        config.second_scale = {
            x_unit: "month", // unit which should be used for second scale
            x_date: "%F %Y" // date format which should be used for second scale, "July 01"
        };
      }
      else if (this.span === 'day') {
        if (this.filter.weekNumbers) {
          config.second_scale = {
              x_unit: "week", // unit which should be used for second scale
              x_date: `${this.$t('timescale.week')} %W` // date format which should be used for second scale, "July 01"
          };
        }
        else {
          config.second_scale = {
              x_unit: "month", // unit which should be used for second scale
              x_date: "%F %Y" // date format which should be used for second scale, "July 01"
          };
        }
      }

      
        
      this.schedulerInstance.createTimelineView(config);

      //Set custom template after timelineView creation
      this.schedulerInstance.templates.timeline_scale_date = function(date) {
        if (self.span === 'week') {
          if (self.filter.weekNumbers) {
            if (self.filter.weekBase === 'calendar_year' || self.projectStartDate == null) {
              return `${self.$t('timescale.week')} ${getWeekNumber(date)}`
            }
            else {
              const start = new Date(self.projectStartDate);
              const week = getWeekNumberProjectStart(start, date);
              if (week > 0) {
                return `${self.$t('timescale.week')} ${week}`
              }
            }
          }
        }
        
        const timeline = self.schedulerInstance.matrix.timeline;
        const func = self.schedulerInstance.date.date_to_str(
            timeline.x_date
            ? timeline.x_date
            : self.schedulerInstance.config.hour_date
        );
        return func(date);        
      }
      this.schedulerInstance.templates.timeline_second_scale_date = function(date) {
        if (self.span === 'day') {
          if (self.filter.weekNumbers) {
            if (self.filter.weekBase === 'calendar_year' || self.projectStartDate == null) {
              return `${self.$t('timescale.week')} ${getWeekNumber(date)}`
            }
            else {
              const start = new Date(self.projectStartDate);
              const week = getWeekNumberProjectStart(start, date);
              if (week > 0) {
                return `${self.$t('timescale.week')} ${week}`
              }
            }
          }
        }
        
        const timeline = self.schedulerInstance.matrix.timeline;
        const func = self.schedulerInstance.date.date_to_str(
            (timeline.second_scale && timeline.second_scale.x_date)
            ? timeline.second_scale.x_date
            : self.schedulerInstance.config.hour_date
        );
        return func(date);        
      }


      let startDate = new Date(this.startDate)
      startDate = new Date(startDate.getTime() + (startDate.getTimezoneOffset() * 60000))
      // this.schedulerInstance.init(this.id,new Date(startDate),"timeline");
      this.schedulerInstance.init(this.id, startDate,"timeline");
      
      this.markedSpans = {};
      for (const timespan of this.markedTimespans) {
        const date = new Date(timespan.start); // for timezone offset
        const s = new Date(timespan.start + (date.getTimezoneOffset() * 60000));
        const id = this.schedulerInstance.addMarkedTimespan({
              start_date: s,
              end_date: new Date(timespan.end + (date.getTimezoneOffset() * 60000)),
              css: `load-marker-weekend id-${timespan.id}`,
              sections: {
                timeline: timespan.key
              }
        });
        
        if (!(timespan.id in this.markedSpans)) {
          this.markedSpans[timespan.id] = [];
        }
        this.markedSpans[timespan.id].push({ 
          id: id,
          name: timespan.name, 
          start: timespan.start, 
          end: timespan.end,
          readonly: timespan.readonly,
          key: timespan.key
        });
      }

      //Re-register 'scroll' listener to new view.
      if (this.onScrollId != null) {
        this.schedulerInstance.detachEvent(this.onScrollId);
      }
      this.onScrollId = this.schedulerInstance.getView().attachEvent('onScroll', function(left, top){
        self.$emit('schedulerScroll', { left: left, top: top });
        if (!self.draggingEvent) {
          // hide the tooltip on scroll
          dhtmlXTooltip.hide();
        }
      });
      this.schedulerInstance.parse(this.data);
      
      this.initialized = true;
      
        // 0 refers to Sunday, 6 - to Saturday
      this.schedulerInstance.ignore_timeline = function(date){
        //hideWeekends flag only applicable to 'day' scale
        if (self.span == 'day' && self.filter.hideWeekends) {
          if (date.getDay() == 6 || date.getDay() == 0) {//hides Saturdays and Sundays
            return true;
          }
        }

        return false;
      };
          
      this.schedulerInstance.templates.timeline_scale_label = function (key, label, section) {
        //const text = label ? label : ' ';
        const expandIcon = section.children && section.open ? '<div class="section-expand"><i class="tree-icon section-expand fa-regular fa-chevron-down"></i></div>' :
                           section.children && !section.open ? '<div class="section-expand"><i class="tree-icon section-expand fa-regular fa-chevron-right"></i></div>' :
                           '<div class="section-expand"><i class="tree-icon hidden-tree-icon section-expand fa-regular fa-chevron-right"></i></div>';
                           
        /*if (section.type === 'staff' ||
            section.type === 'activity' ||
            section.type === 'booking' ||
            section.type === 'task') {*/
            setTimeout(() => {
            const elem = document.querySelector(`[data-row-id="${section.key}"]`);
            if (elem) {
              const index = elem ? parseInt(elem.getAttribute('data-row-index')) : -1;
              let className = '';
              if (index % 2) {
                // odd
                className = 'scheduler-odd-row';
              }
              else {
                className = 'scheduler-even-row';
              }
              elem.firstChild.classList.add(className);
            }
          }, 100);
        //}                   
        
        if ((typeof section === 'object' && section.uuId.startsWith('('))) {
          return `${expandIcon}<div class="section-expand"><div class="tag ${self.getTagClass(section)}">${section.type ? self.getTagLabel(section) : 'Staff'}</div><div class="scheduler-tag-nolink" href="#" id="${key}">${label}</div></div>`;
        }
        return `${expandIcon}<div class="section-expand"><div class="tag ${self.getTagClass(section)}">${section.type ? self.getTagLabel(section) : 'Staff'}</div><a class="scheduler-tag-link" href="#" id="${key}">${label}</a></div>`;
      };
      
      this.schedulerInstance.templates.event_bar_text=function(start,end,event){
        var bookingevs = event.type === 'booking' && self.filter.alertBookings ? event.hasOverlap : null;
        var activityevs = event.type === 'activity' && self.filter.alertActivities ? event.hasOverlap : null;
        var nonworkevs = event.type === 'vacation' && self.filter.alertNonWork ? event.hasOverlap : null;
        var taskevs = event.type === 'task' && self.filter.alertTasks && event.type === 'task' ? event.hasOverlap : null;  
       
        // bookingevs and taskevs contain the task and booking itself so the count is always at least 1
        self.bookingevs = bookingevs ? bookingevs : null;
        self.activityevs = activityevs ? activityevs : null;
        self.taskevs =  taskevs ? taskevs : null;
        self.nonworkevs = nonworkevs ? nonworkevs : null;
        const section = self.schedulerInstance.getSection(event.section_id);
        const readonly = event.readonly || self.lockPlanner ? "<i class='readonly-icon far fa-lock'></i>" : '';
        const warn = (self.filter.alertTaskBookings && event.type === 'task' && !event.booking) ||
                     // add one day to until so that we count the next day
                     (event.end_date.getTime() > (section.until + 86400000) || event.start_date.getTime() < section.begin) ||
                     (self.filter.alertBookings && event.type === 'booking' && bookingevs) ||
                     (self.filter.alertActivities && event.type === 'activity' && activityevs) ||
                     (self.filter.alertNonWork && event.type === 'vacation' && nonworkevs) ||
                     (self.filter.alertTasks && taskevs) ? 
                     (event.dotcolor && !self.filter.fullBarColor ? `<i class='warning-icon fas fa-triangle-exclamation'></i><div class="tag type-color" style="background-color:${event.dotcolor}"></div>` :
                     "<i class='readonly-icon fas fa-triangle-exclamation'></i>") : 
                     event.dotcolor && !self.filter.fullBarColor ? `<div class="tag type-color" style="background-color:${event.dotcolor}"></div>` : '';
        
        return `<div style="${self.getProgressStyle(event)}"><div class="event-text">${readonly}${warn}${event.text}</div></div>`;
      };
      
      this.schedulerInstance.filter_timeline = function(id, event) {
  
        // default, do not display event
        return self.shouldAdd(event);
      };
      
      this.schedulerInstance.templates.timeline_cell_value = function folderCellContent(evs, date, section){
        
        var className = "";
        var containerClassName = "";
        var name = "";
        var type = "";
        /*if (section.type === 'staff' ||
            section.type === 'activity' ||
            section.type === 'booking' ||
            section.type === 'task' ||
            section.type === 'resource') {*/
          const elem = document.querySelector(`[data-row-id="${section.key}"]`);
          if (elem) {
            const index = elem ? parseInt(elem.getAttribute('data-row-index')) : -1;
            
            if (index % 2) {
              // odd
              if (self.filter.usage) {
                containerClassName = 'scheduler-odd-row';
              }
              else {
                className = 'scheduler-odd-row';
              }
            }
            else {
              if (self.filter.usage) {
                containerClassName = 'scheduler-even-row';
              }
              else {
                className = 'scheduler-even-row';
              }
            }
          }
        //}
                
        // show today using the var(--today) color
        if (isToday(date, self.span)) {
          className = "today";
          const e = document.querySelector(`[data-col-date="${date.getFullYear()}-${('0' + (date.getMonth()+1)).slice(-2)}-${('0' + date.getDate()).slice(-2)} 00:00"]`);
          if (e) {
            const idx = e.getAttribute('data-col-id');
            const headerElem = document.querySelector(`[data-col-id="${parseInt(idx)}"]`);
            if (headerElem) {
              headerElem.classList.add('today');
            }
          }
        }
         
        if (self.filter.usage) {
          let ref = getDateRef(date, self.span);
          let notEmployed = false;
          
          // clear out the tasks map
          tasksMap[ref+section.key] = [];
          const quota = section.quota ? section.quota : 1;
          const projectId = self.filterByProject ? section.projectUuId : 
                            self.projectId;
          const w = self.calculateUsage(date, section, projectId);
          if (w) {             
            if (self.span === 'day' &&
                self.isNonWorkingDay(date, section)) {
              className = "load-marker-weekend";
            }
            else if((!w.a || w.a === -1) && section.type === 'staff') {
              const endDate = self.getEndDate(date);
              if (section.type === 'staff' &&
                  ((section.begin - 86400000) > date.getTime() || // subtract 1 day so that the start date is shown as employed
                   section.until < endDate.getTime()) &&
                   ((w && w.t && w.t.length === 0) || self.badAssign(section.begin, section.until, w && w.t ? w.t : [])) &&
                   self.checkDuplicateStaffDates(section.duplicateStaff, date)) {
                if (w && w.t && w.t.length > 0) {
                  if (self.badAssign(section.begin, section.until, w.t)) {
                    className = 'load-marker-bad-assign';
                    //w.a = -1; // show not employed in the tooltip
                    notEmployed = true;
                    containerClassName = 'load-marker-not-employed';
                  }
                }
                else {
                  className = 'load-marker-not-employed';
                  //w.a = -1;
                  notEmployed = true;
                }
              }
              else {
                className = "load-marker-no";
              }
            } else {
              let workingHours = self.getWorkingValue(w);
              const availableHours = w && w.a ? w.a * quota : 0;
              
              if (w && w.a === -2 && section.type === 'staff') {
                className = "load-marker-weekend";
              }
              else if (self.isCost(w) && !self.filter.capacity) {
                if (self.costAlert(w, quota)) {
                  className = 'load-marker-over';
                }
                else {
                  className = 'load-marker-full';
                }
              }
              else if ((self.staffAlloc === 'fixeddurationhours' ||
                        self.staffAlloc === 'fixeddurationdays') &&
                        workingHours > 0 &&
                        !self.filter.capacity) {
                className = 'load-marker-full';        
              }
              else if ((workingHours <= availableHours &&
                        !self.zeroCost(w)) ||
                        self.filter.capacity) {
                let percent = 0;
                
                if (self.filter.required || (section.generic && !self.filter.capacity)) {
                    // do not show availablity for generic
                    percent = Math.round(workingHours / availableHours * 100);
                }
                else if (self.filter.available && availableHours !== 0) {
                  percent = 100 - Math.round(workingHours / availableHours * 100);
                }
                else if ((self.staffAlloc === 'estcost'  || self.staffAlloc === 'estcostnet') && w.capacityCost === 0) {
                  percent = 0;
                }
                else if (self.filter.capacity && availableHours !== 0 &&
                         self.staffAlloc !== 'fixedcost' &&
                         self.staffAlloc !== 'fixedcostnet' &&
                         self.staffAlloc !== 'actualcost' &&
                         self.staffAlloc !== 'actualcostnet' &&
                         self.staffAlloc !== 'fixeddurationhours' &&
                         self.staffAlloc !== 'fixeddurationdays' &&
                         self.staffAlloc !== 'actualdurationhours' &&
                         self.staffAlloc !== 'actualdurationdays') {
                  percent =  Math.round(availableHours / w.defaultAvailability * 100);
                }
                              
                if (percent === 0) {
                  if (self.filter.available && !section.generic) {
                    className = 'load-marker-over';
                  }
                }
                else if (self.showUnders && percent <= 25) {
                  className = 'load-marker-low';
                }
                else if (self.showUnders && percent <= 50) {
                  className = 'load-marker-med';
                }
                else if (self.showUnders && percent <= 75) {
                  className = 'load-marker-high';
                }
                else if ((self.showOptimal && percent >= 76 && percent <= 100) ||
                         (self.filter.capacity && availableHours > 0 && workingHours >= 0)) {
                  className = 'load-marker-full';
                }
              } else if (!self.zeroCost(w) && self.showOvers && availableHours > 0 && workingHours > 0) {
                let overpercent = Math.trunc(workingHours / availableHours * 100);
                  
                if (overpercent !== 0) { 
                  if (overpercent <= 125) {
                    className = 'load-marker-over';
                  }
                  else if (overpercent <= 150) {
                    className = 'load-marker-over2';
                  }
                  else {
                    className = 'load-marker-over3';
                  }
                }
              }
            }
            
            if (className === '') {
              return '';
            }
            
            if (!w.w && !notEmployed && (self.filter.required || (section.generic && !self.filter.capacity))) {
              return "<div class='"+containerClassName+"'><div class='load-marker "+className+"' data-name='"+section.name.replace(/'/, '&apos;') +"' data-usage='"+(w.w ? w.w : 0)+"'  data-availability='"+(w.a ? w.a : 0)+"' data-headcount='" + w.h +"' data-headcountavail='" + (w.ha * (section.quota ? section.quota : 1)) +"' data-type='" + (section.type ? section.type : "staff") + "' data-generic='" + section.generic + "' data-tasksid='"+ref+section.key+"'>"+
              "</div></div>";
            }
            else if (className === 'load-marker-bad-assign') {
              tasksMap[ref+section.key] = w.t ? w.t : [];
              const usageValue = className.includes('load-marker') && !className.includes('weekend') && !className.includes('load-marker-no') ? self.label(self.filter.capacity ? w.a : self.getWorkingValue(w), w.a ? w.a * quota : 0, w.ga * quota, w.t, section.type, w.h, (w.ha * quota), w) : '';

              return "<div class='"+containerClassName+"'><div class='load-marker "+className+"' data-name='"+section.name.replace(/'/, '&apos;') +"' data-usage='"+(w.w ? w.w : 0)+"'  data-availability='-1' data-headcount='" + w.h +"' data-headcountavail='" + (w.ha * (section.quota ? section.quota : 1)) +"' data-type='" + (section.type ? section.type : "staff") + "' data-generic='" + section.generic + "' data-tasksid='"+ref+section.key+"'>"+
              "<i class='fas fa-exclamation-triangle ml-1 mr-1 pointer-events-none' style='color: red;font-size: 1.125em;'></i>" +
              usageValue +
              "</div></div>";
            }
            
            // store the tasks so that a tooltip can reference them
            tasksMap[ref+section.key] = w.t ? w.t : [];
            
            const usageValue = className.includes('load-marker') && !className.includes('load-marker-not-employed') && !className.includes('weekend') && !className.includes('load-marker-no') ? self.label(self.filter.capacity ? w.a : self.getWorkingValue(w), w.a ? w.a * quota : 0, w.ga * quota, w.t, section.type, w.h, (w.ha * quota), w) : '';
            return "<div class='"+containerClassName+"'><div class='load-marker "+className+"' data-name='"+section.name.replace(/'/, '&apos;') +"' data-usage='"+(w.w ? w.w : 0)+"'  data-availability='"+(notEmployed ? -1 : w.a)+"' data-headcount='" + w.h +"' data-headcountavail='" + (w.ha * (section.quota ? section.quota : 1)) +"' data-type='" + (section.type ? section.type : "staff") + "' data-generic='" + section.generic + "' data-tasksid='"+ref+section.key+"'>"+
              usageValue
              +"</div></div>"
              + self.overAlloc(w.c, className);
    
          }
          else if (self.span === 'day' &&
              self.isNonWorkingDay(date, section) &&
              section.type === 'staff') {
            className = "load-marker-weekend";
            name = self.$t(`weekday.${dayNames[date.getDay()]}`);
            type = 'vacation';
          }
    
        }
        else if (self.span === 'day' &&
                self.isNonWorkingDay(date, section) &&
              (section.type === 'staff' || section.type === 'resource')) {
          className = "load-marker-weekend";
            name = self.$t(`weekday.${dayNames[date.getDay()]}`);
            type = 'vacation';
        }
        else if (section.type === 'staff' &&
                (section.begin - 86400000> date.getTime() ||
                 section.until < date.getTime()) &&
                 self.checkDuplicateStaffDates(section.duplicateStaff, date)) {
          className = 'load-marker-not-employed';
          return "<div class='"+containerClassName+"'><div class='load-marker "+className+"' data-name='"+section.name.replace(/'/, '&apos;') +"' data-availability='-1' data-type='" + (section.type ? section.type : "staff") + "' data-generic='" + section.generic + "'>"+
            "</div></div>";
        }
        
        if (className) {
          return "<div class='"+className+"' data-name='" + name.replace(/'/, '&apos;') + "' data-type='" + type + "' data-date='" + date.getTime() + "'></div>";
        }
        return "";
      };
      
      this.schedulerInstance.setCurrentView();
        
      if (scrolling) {
        setTimeout(() => {
          const newScrolling = document.querySelector('.dhx_timeline_scrollable_data');
          if (newScrolling) {
            newScrolling.scrollTo(scrollLeft, scrollTop);
            newScrolling.scrollTop = scrollTop;
            newScrolling.scrollLeft = scrollLeft;
          }
        }, 500);
      }
        
      document.addEventListener("keydown", self.keyDown);
      
      /* eslint-enable no-undef */
    },
    resolveStaffStartAndEnd(section, timestamp) {
      const staff = [section];
      if (section.duplicateStaff) {
        staff.push(...section.duplicateStaff);
      }
      
      if (!staff || 
          this.filter.singleContracts) {
        return section; // no duplicates
      }
      
      for (const stf of staff) {
        if ((stf.begin - 86400000) > timestamp || // subtract 1 day so that the start date is shown as employed
                   stf.until < timestamp) {
          // staff is not employed          
        }
        else {
          // staff is employed
          return stf;
        }
      }
      return section;
    },
    checkDuplicateStaffDates(staff, date) {
      if (!staff || 
          this.filter.singleContracts) {
        return true; // no duplicates
      }
      
      for (const stf of staff) {
        if ((stf.begin - 86400000) > date.getTime() || // subtract 1 day so that the start date is shown as employed
                   stf.until < date.getTime()) {
          // staff is not employed          
        }
        else {
          // staff is employed
          return false;
        }
      }
      return true;
    },
    isCost(w) {
      return (
          ((this.staffAlloc === 'estcost' || 
          this.staffAlloc === 'estcostnet') &&
          w.cost !== 0) || 
          ((this.staffAlloc === 'fixedcost' || 
          this.staffAlloc === 'fixedcostnet') &&
          w.fixedCost !== 0) || 
          ((this.staffAlloc === 'actualcost' || 
          this.staffAlloc === 'actualcostnet') &&
          w.actualCost !== 0)
      );
    },
    zeroCost(w) {
      return (
          ((this.staffAlloc === 'estcost' || 
          this.staffAlloc === 'estcostnet') &&
          w.cost === 0) || 
          ((this.staffAlloc === 'fixedcost' || 
          this.staffAlloc === 'fixedcostnet') &&
          w.fixedCost === 0) || 
          ((this.staffAlloc === 'actualcost' || 
          this.staffAlloc === 'actualcostnet') &&
          w.actualCost === 0)
      );
    },
    overAlloc(c, className) {
      if (c && this.showOvers && !className.includes('load-marker-over')) {
        return `<div class="menu-icon-toggler" style="top: 0; right: 0; width: 11px; height: 100%;">
              <div class="stafftriangle stafftriangle_tr"></div>
          </div>`;
      }
      return '';
    },
    addLinkClickListener() {
      const links = document.querySelector('.dhx_timeline_label_wrapper');//.scheduler-tag-link
      if (links) {
        links.addEventListener('click', this.onClickLink);
      }
    },
    removeLinkClickListeners() {
      const links = document.querySelectorAll('.scheduler-tag-link');
      if (links) {
        for (const link of links) {
          link.removeEventListener('click', this.onClickLink);
        }
      }
    },
    addWheelListener() {
      const sectionWrapper = document.querySelector('.dhx_timeline_label_wrapper');
      if (sectionWrapper) {
        sectionWrapper.addEventListener('wheel', debounce(function(e) {
          
          const scrollable = document.querySelector('.dhx_timeline_scrollable_data');
          scrollable.scrollTo(scrollable.scrollLeft, scrollable.scrollTop + e.deltaY);
        }, 5), { passive: true});

        sectionWrapper.addEventListener("touchstart", function (e) {
          var touch = e;
          if (e.touches) {
            touch = e.touches[0];
          }
          self.prevAction = { pageX: touch.pageX, pageY: touch.pageY };
        }, { passive: true});
        
        setTimeout(() => {
          sectionWrapper.addEventListener('touchmove', function(e) {
            var touch = e;
            e.preventDefault();
            if (e.touches) {
              touch = e.touches[0];
            }
            
            // var dx = self.prevAction.pageX - touch.pageX;
            var dy = self.prevAction.pageY - touch.pageY;
            self.prevAction = { pageX: touch.pageX, pageY: touch.pageY };
            const scrollable = document.querySelector('.dhx_timeline_scrollable_data');
            scrollable.scrollTo(scrollable.scrollLeft, scrollable.scrollTop + dy);
          });
        }, 500);
      }
    },
    onClickLink(e) {
      if (e.target.className === 'scheduler-tag-nolink') {
        return;
      }
      
      /* eslint-disable no-undef */
      const section = this.schedulerInstance.getSection(e.target.id);
      /* eslint-enable no-undef */
      if (section) {
        const id = e.target.id;
        this.$emit('clickItem', {id: id.slice(id.length - 36), data: section });
        this.popupRemoveMenu();
        e.stopPropagation();
      }
    },
    getExportLabel(data) {
      if (data.type) {
        if (data.type === 'staff' && data.generic) {
          return this.$t('staff.generic_staff');
        }
        return this.$t(`staff.group.${data.type}`);
      }
      return this.$t(`staff.group.staff`);
    },
    getTagLabel(data) {
      if (data.type) {
        if (data.type === 'staff' && data.generic) {
          return this.$t('staff.group.generic');
        }
        return this.$t(`staff.group.${data.type}`);
      }
      return this.$t(`staff.group.staff`);
    },
    getTagClass(data) {
    
      if (data && data.type) {
        if (data.type === 'company') {
          return 'tag-company';
        }
        else if (data.type === 'location') {
          return 'tag-location';
        }
        else if (data.type === 'stage') {
          return 'tag-stage';
        }
        else if (data.type === 'project') {
          return 'tag-project';
        }
        else if (data.type === 'department') {
          return 'tag-department';
        }
        else if (data.type === 'skill') {
          return 'tag-skill';
        }
        else if (data.type === 'task') {
          return 'tag-task';
        }
        else if (data.type === 'activity') {
          return 'tag-activity';
        }
        else if (data.type === 'booking') {
          return 'tag-booking';
        }
        else if (data.type === 'staff' && data.generic) {
          return 'tag-staff-generic';
        }
        else if (data.type === 'vacation') {
          return 'tag-vacation';
        }
        else if (data.type === 'new') {
          return 'tag-new';
        }
        else if (data.type === 'resource' ||
                 data.type === 'resourceUnit') {
          return 'tag-resource';
        }
      }
      
      return 'tag-staff';
    },
    getWorkingValue(w) {
      if (this.staffAlloc === 'fixeddurationhours' ||
          this.staffAlloc === 'fixeddurationdays') {
        return w.wFixed;
      }
      else if (this.staffAlloc === 'actualdurationhours' ||
               this.staffAlloc === 'actualdurationdays') {
        return w.wActual;
      }
      return w.w ? w.w : 0;
    },
    label(value, a, ga, t, type, h, ha, w, fullcost = false) {
      if (this.filter.capacity && (this.staffAlloc === 'fixedcost' ||
                         this.staffAlloc === 'fixedcostnet' ||
                         this.staffAlloc === 'actualcost' ||
                         this.staffAlloc === 'actualcostnet' ||
                         this.staffAlloc === 'fixeddurationhours' ||
                         this.staffAlloc === 'fixeddurationdays' ||
                         this.staffAlloc === 'actualdurationhours' ||
                         this.staffAlloc === 'actualdurationdays')) {
        return '';
      }
      
      const staffAlloc = this.staffAlloc;
      const showHeadCount = staffAlloc === 'headcount';
      if (showHeadCount) {
        if (h !== 0 || !this.filter.required) {
          return Math.ceil(parseFloat(this.filter.required ? h : this.filter.available ? ha - h : ha).toFixed(2).replace(/\.?0+$/, ''));
        }
        else {
          return '0';
        }
      }
      else if (staffAlloc === 'percent') {
        const v = (value % 1 !== 0 ? parseFloat(value).toFixed(1) : value);
        if (a === 0) {
          if (ga) {
            return (this.filter.required ? Math.round(v / ga * 100) : this.filter.available ? 100 - Math.round(v / ga * 100) : Math.round(ga / v * 100)) + '%';
          }
          return '0%';
        }
        else if (a === -1) {
          return '';
        }
        return (this.filter.required ? Math.round(v / a * 100) : this.filter.available ? 100 - Math.round(v / a * 100) : Math.round(a / v * 100))  + '%';
      }
      else if (staffAlloc === 'hours' || staffAlloc === 'fixeddurationhours' || staffAlloc === 'actualdurationhours') {
        return value === null || (value !== null && value !== 0) || !this.filter.required ? (value % 1 !== 0 ? `${parseFloat((this.filter.required ? value : a !== -1 ? (this.filter.available ? a - value : a) : 0)).toFixed(4).replace(/\.?0+$/, '')}h` : this.filter.capacity ? `${value}h` : `${this.valueWorkingHours(value, a, ga, t, type)}h`) : '';
      }
      else if (staffAlloc === 'days' || staffAlloc === 'fixeddurationdays' || staffAlloc === 'actualdurationdays') {
        return value === null || (value !== null && value !== 0) || !this.filter.required ? `${parseFloat((this.filter.required ? value : a !== -1 ? (this.filter.available ? a - value : a) : 0) / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D` : '';
      }
      else if (staffAlloc === 'estcost' || staffAlloc === 'fixedcost' || staffAlloc === 'actualcost') {
        const costValue = staffAlloc === 'estcost' ? (this.filter.capacity ? w.capacityCost : w.cost) : staffAlloc === 'fixedcost' ? w.fixedCost : w.actualCost;
        const currencyCode = w.code;
        if (fullcost) {
          return costValue !== 0 ? `${this.getCurrencySymbol(currencyCode)}${toFixed(costValue, 0)}` : '';
        }
        return costValue !== 0 ? costFormatAdv(costValue, currencyCode ? currencyCode : 'USD') : '';
      }
      else if (staffAlloc === 'estcostnet' || staffAlloc === 'fixedcostnet' || staffAlloc === 'actualcostnet') {
        const currencyCode = w.code;
        const costNetValue = staffAlloc === 'estcostnet' ? (this.filter.capacity ? w.capacityCost : w.costNet) : staffAlloc === 'fixedcostnet' ? w.fixedCostNet : w.actualCostNet; 
        if (fullcost) {
          return costNetValue !== 0 ? `${this.getCurrencySymbol(currencyCode)}${toFixed(costNetValue, 0)}` : '';
        }
        return costNetValue !== 0 ? costFormatAdv(costNetValue, currencyCode ? currencyCode : 'USD') : '';
      }
      
      return '';
    },
    getCurrencySymbol(code) {
      if (!code) {
        return '';
      }
      
      return costFormatAdv(0, code).replace(/\d/g, '').trim()
    },
    
    valueWorkingHours(value, a, ga, t) {
      if (!this.staffUsageProject ||
          typeof t === 'undefined') {
        return value || !this.filter.required ? (this.filter.required ? value : a !== -1 ? a - value : 0) : 0;
      }
      else {
        let hours = 0;
        const list = this.taskAllocationList(t);
        for (let i = 0; i < list.length; i++) {
          if (list[i].w) {
            hours += list[i].w;
          }
        }
        return hours;
      }
    },
    taskAllocationList(t) {
      const list = t ? t : null;
      var tasks = [];
      if (list) {
        tasks = this.getTasks(list);
      }
      return tasks;
    },
    getDateLabel() {
      if (this.span === 'day') {
        return "%D, %M %j";
      }
      else if (this.span === 'week') {
        if (this.filter.weekNumbers) {
          return `${this.$t('timescale.week')} %W`;
        }
        return "%M %d";
      }
      else if (this.span === 'month') {
        return "%M, %Y";
      }
      else if (this.span === 'year') {
        return "%Y";
      }
    },
    monthDiff(d1, d2) {
      var months;
      months = (d2.getFullYear() - d1.getFullYear()) * 12;
      months -= d1.getMonth();
      months += d2.getMonth();
      return months <= 0 ? 0 : months + 1;
    },
    getSize() {
      const end = new Date(this.endDate);
      const start = new Date(this.startDate);
      let difference = end.getTime() - start.getTime();
      if (this.span === 'day') {
        return Math.ceil(difference / (1000 * 3600 * 24)) + 1;
      }
      else if (this.span === 'week') {
        return Math.ceil(difference / (1000 * 3600 * 24 * 7)) + 1;
      }
      else if (this.span === 'month') {
        return this.monthDiff(start, end);
      }
      else if (this.span === 'year') {
        return Math.ceil(difference / (1000 * 3600 * 24 * 365));
      }
    },
    onClickScheduler(e) {
      var event = e || window.event;
      const target = event.target || event.srcElement;
      const overAlloc = target.className === "stafftriangle stafftriangle_tr";
      const tasks = target.hasAttribute('data-tasksid') ? tasksMap[target.getAttribute('data-tasksid')] : null;
      
      if (tasks) {
        this.$emit('showtasks', tasks);
        this.popupRemoveMenu();
      }
      
      if (overAlloc) {
        this.$emit('gotoday');
      }
    },
    onMouseOver(e) {
      var event = e || window.event;
      
      if (this.popupElement) {
        this.popupElement.removeEventListener('mouseleave', this.popupRemoveMenu);
        this.popupElement.remove();
        delete this.popupElement;
      }
      var target = event.target || event.srcElement;
      if (!this.draggingEvent) {
        this.renderPopup(target);
      }
    },
    onMouseLeave(/**e*/) {
      this.popupRemoveMenu();
      /* eslint-disable no-undef */
      dhtmlXTooltip.hide();
      /* eslint-enable no-undef */
    },
    htmlToElement(html) {
      var template = document.createElement('template');
      html = html.trim(); // Never return a text node of whitespace as the result
      template.innerHTML = html;
      return template.content.firstChild;
    },
    formatTasks(tasks, a /**, type */) {
      if (!tasks) {
        return '';
      }
      
      let ret = '';
      for (let i = 0; i < tasks.length; i++) {
        if (i < 8) {
          const w = tasks[i].w ? tasks[i].w : 0;
          const h = a !== 0 ? Math.ceil(parseFloat(w / getDefaultAvailability(this.span, this.durationConversionOpts)).toFixed(4).replace(/\.?0+$/, '')) : 0;
          ret += `<p>
              <span class="tag ${this.getTagClass({ type: tasks[i].type, generic: false })}">${this.getTagLabel({ type: tasks[i].type })}</span><span>${tasks[i].path ? tasks[i].path.replace(/\n/g, ' / ') : tasks[i].name}: </span>
              <span>${this.formatValue(w, a, h)}</span>
            </p>`;
        }
      }
      return ret;
    },
    getParentElement(elem) {
      if (elem.classList.contains('grid-stack-item')) {
        return elem;
      }
      return this.getParentElement(elem.parentElement);
    },
    renderPopup(target) {
      const overAlloc = target.className === "stafftriangle stafftriangle_tr";
      const markedSpan = target.className.indexOf && target.className.indexOf('dhx_marked_timespan') !== -1 && target.className.indexOf('dhx_matrix_now_time') === -1;
      const staffLink = target.className.indexOf && target.className.indexOf("scheduler-tag-link") !== -1;
      let avatartag = '';
      let position = '';
      let staffType = '';
      let location = '';
      let department = '';
      let skills = '';
      let company = '';
      let quota = '';
      let begin = 0;
      let until = 0;
      let start = 0;
      let end = 0;
      let dhxScaleBar = target.className === 'dhx_scale_bar ' && this.span === 'week';
      let name = target.getAttribute('data-name');
      if (name) {
        name = name.replace(/&apos;/, '\'');
      }
      const usage = target.hasAttribute('data-usage') ? target.getAttribute('data-usage') : 0;
      const headcount = target.hasAttribute('data-headcount') ? target.getAttribute('data-headcount') : 0;
      const headcountavail = target.hasAttribute('data-headcountavail') ? target.getAttribute('data-headcountavail') : 0;
      let type = target.hasAttribute('data-type') ? target.getAttribute('data-type') : null;
      start = target.hasAttribute('data-date') ? parseInt(target.getAttribute('data-date')) : 0;
      let generic = target.hasAttribute('data-generic') ? target.getAttribute('data-generic') === 'true' : false;
      const availability = target.hasAttribute('data-availability') ? target.getAttribute('data-availability') : 0;
      const tasks = target.hasAttribute('data-tasksid') ? tasksMap[target.getAttribute('data-tasksid')] : [];
      
      if (staffLink) {
        /* eslint-disable no-undef */
        const section = scheduler.getSection(target.getAttribute('id'));
        /* eslint-enable no-undef */
        type = section.type;
        if (section.type === 'staff' || section.type === 'resource') {
          const avatar = section.avatar && section.avatar !== '00000000-0000-0000-0000-000000000000' ? `${process.env.BASE_URL}api/file/${section.avatar}` : null;
          if (avatar) {
            avatartag = `<div class="section-expand avatar-align"><img class="img-avatar smaller" style="object-fit:cover" src="${avatar}"/></div>`;
          }
          name = section.name;
          if (!(section.type === 'resource' && this.resourceField === 'resourceUuid')) {
            position = section.position;
            staffType = section.staffType;
            location = section.location;
            department = section.departmentList ? section.departmentList.map(d => { return d.name }).join(', ') : section.department;
            skills = section.skillList ? section.skillList.map(s => { return s.name }).join(', ') : '';
            company = section.company;
            begin = section.begin;
            until = section.until;
            generic = section.generic;
          }
          quota = section.quota;
        }
      }
      
      if (markedSpan) {
        // get the id from the classlist
        const classes = target.className.split(' ');
        for (const className of classes) {
          if (/id-.*/.test(className)) {
            const id = className.match(/id-(.+?$)/);
            if (id && id[1] in this.markedSpans) {
              name = this.markedSpans[id[1]][0].name;
              start = this.markedSpans[id[1]][0].start;
              end = this.markedSpans[id[1]][0].end;
              type = 'vacation';
              
              // const date = new Date();
              start = moment(start).format('ddd, YYYY-MM-DD');
              end = moment(end).format('ddd, YYYY-MM-DD');
            }
            break;
          }
        }
      }
      
      if (!name && !overAlloc && !markedSpan && !dhxScaleBar) {
        return;
      }
      const container = document.getElementById(this.id);
      const rect = target.getBoundingClientRect();
      const contrainerRect = container.getBoundingClientRect();
      const left = rect.left - contrainerRect.left + 15;
      let top = rect.top - 180;

      if (this.isWidget) {
        const parentContainer = this.getParentElement(target);
        top = (event.y + (window.scrollY - parentContainer.offsetTop)) - 390;
      }
      
      if (overAlloc) {
        top = top - 60;
      }
      
      const containerHeight = container.offsetTop + container.offsetHeight;
      const menuItems = `<div class="staffpopupmenuoption ag-menu-option" tabindex="-1" role="treeitem" aria-level="1" data-action="goto-day">
                      <span ref="eIcon" class="ag-menu-option-icon" role="presentation"></span>
                      <span ref="eName" class="staffpopupmenutext">${this.$t('staff.overs_timescale_msg')}</span>
                  </div>`;
      const htmlContentStr = dhxScaleBar ? 
      `
        <div class="cell-tooltip ag-menu ag-focus-managed ag-ltr ag-popup-child" role="dialog" aria-label="Column Menu" >
          <div class="staffpopupheader-box">
            <div class="staffpopupheader ml-1">${target.innerText}</div>
          </div>
          <div role="presentation" class="ag-tabs-body ag-menu-body">
              <div class="ag-focus-managed ml-1 mr-1" role="tree">
                  <div class="ag-tab-guard ag-tab-guard-top" role="presentation" tabindex="0"></div>
                  ${moment(target.getAttribute('data-col-date')).format('L')} - ${moment(target.getAttribute('data-col-date')).isoWeekday(5).format('L')}
                  <div class="ag-tab-guard ag-tab-guard-bottom" role="presentation" tabindex="0"></div>
              </div>
          </div>
      </div>      
      `
      : overAlloc ? 
      `
        <div class="cell-tooltip ag-menu ag-focus-managed ag-ltr ag-popup-child" role="dialog" aria-label="Column Menu" >
          <div class="staffpopupheader-box">
            <i style="font-size: 1.125em;" class="ml-1 mr-1 d-flex fa-regular fa-exclamation-triangle"></i>
            <div class="staffpopupheader">${this.$t('staff.overs_timescale')}</div>
          </div>
          <div role="presentation" class="ag-tabs-body ag-menu-body">
              <div class="ag-focus-managed" role="tree">
                  <div class="ag-tab-guard ag-tab-guard-top" role="presentation" tabindex="0"></div>
                  ${menuItems}
                  <div class="ag-tab-guard ag-tab-guard-bottom" role="presentation" tabindex="0"></div>
              </div>
          </div>
      </div>
      `
      :
      type === 'vacation' && end === 0 ?
      
      `
        <div 
          class="cell-tooltip ag-tooltip-custom ag-ltr ag-popup-child" 
          role="dialog" 
          aria-label="Tooltip" >
          <div data-v-409e30f4="" class="header-box">
            <div class="tag ${this.getTagClass({ type: type, generic: generic })}">${this.getTagLabel({ type: type })}</div>
            <div class="header">${name}</div>
          </div>
          <div>
            <p><span>${this.$t('label.date')}: ${new Date(start - (new Date().getTimezoneOffset() * 60000)).toISOString().split('T')[0]}</span></p>
          </div>
        </div>
      `
      :
      type === 'vacation' ?
      
      `
        <div 
          class="cell-tooltip ag-tooltip-custom ag-ltr ag-popup-child" 
          role="dialog" 
          aria-label="Tooltip" >
          <div data-v-409e30f4="" class="header-box">
            <div class="tag ${this.getTagClass({ type: type, generic: generic })}">${this.getTagLabel({ type: type })}</div>
            <div class="header">${name}</div>
          </div>
          <div>
            <p><span class="dhtmlXTooltipStartTime">${this.$t('task.field.startTime')}: ${start}</span></p>
            <p><span class="dhtmlXTooltipCloseTime">${this.$t('task.field.closeTime')}: ${end}</span></p>
          </div>
        </div>
      `
      :
      staffLink ?
      `
        <div 
          class="cell-tooltip ag-tooltip-custom ag-ltr ag-popup-child" 
          role="dialog" 
          aria-label="Tooltip" >
          <div data-v-409e30f4="" class="header-box">
            <div class="tag ${this.getTagClass({ type: type, generic: generic })}">${this.getTagLabel({ type: type, generic: generic })}</div>
            <div class="header">${name}</div>
          </div>
          ${avatartag}
          <div class="d-inline-block">
            ${this.formatStaff(position, staffType, location, department, skills, company, begin, until, quota, generic)}
          </div>
        </div>
      `
      :
      `
        <div 
          class="cell-tooltip ag-tooltip-custom ag-ltr ag-popup-child" 
          role="dialog" 
          aria-label="Tooltip" >
          <div data-v-409e30f4="" class="header-box">
            <div class="tag ${this.getTagClass({ type: type, generic: generic })}">${this.getTagLabel({ type: type })}</div>
            <div class="header">${name}</div>
          </div>
          <div>
            ${this.formatTasks(tasks, availability, type)}
            <p>
              <span>${this.$t('filter_component.usage')}: </span>
              <span>${this.formatValue(usage, availability, headcount, type)}</span>
            </p>
            <p>
              <span>${this.$t('filter_component.availability')}: </span>
              ${availability !== "-1" ? this.formatValue(availability, availability, headcountavail, type) : this.$t('staff.not_employed')}
            </p>
          </div>
        </div>
      `;
      let flipTop = false;
      //Assuming standard tooltip height is 87. 18 is the height of row cell + buffer
      if (top + 105 > containerHeight) {
        flipTop = true;
        //87 + 43 (offset) when flip to top
        top -= 130;
      }
      const cellMenu = this.htmlToElement(htmlContentStr.trim().replaceAll(/>\n[\s]+/g, '>'));
      cellMenu.style.left = overAlloc ? `${left - 50}px` : `${left - 15}px`;
      cellMenu.style.top = `${top}px`;
      let popupElement = null;
      popupElement = document.createElement('div');
      popupElement.classList.add('ag-theme-balham')
      popupElement.classList.add('ag-popup');
      popupElement.classList.add('header-group-column-popup');
      popupElement.appendChild(cellMenu);
      document.querySelector(`#${this.id}`).appendChild(popupElement);

      if (popupElement.firstChild.offsetLeft + popupElement.firstChild.offsetWidth > container.offsetWidth) {
        cellMenu.style.left = `${container.offsetWidth - popupElement.firstChild.offsetWidth}px`;
      }
      
      //Adjust top according to actual height
      if (flipTop && cellMenu.clientHeight != 87 && !overAlloc) {
        cellMenu.style.top = `${top - (cellMenu.clientHeight - 87)}px`;
      }
      
      this.popupElement = popupElement;
      popupElement.addEventListener('mouseleave', this.popupRemoveMenu);
      
      if (overAlloc) {
        target.addEventListener('click', this.popupMenuClick);
      }
    },
    popupRemoveMenu() {
        
      if (this.popupElement) {
        this.popupElement.removeEventListener('mouseleave', this.popupRemoveMenu);
        this.popupElement.remove();
        delete this.popupElement;
      }
      
    },
    popupMenuClick(e) {
      e.target.removeEventListener('click', this.popupMenuClick);
      if (this.popupElement) {
        this.popupElement.removeEventListener('mouseleave', this.popupRemoveMenu);
        this.popupElement.remove();
        delete this.popupElement;
      }
      this.$emit('gotoday');
    },
    formatValue(value, a, headcount) {
      if (value === "-1") {
        return '??';
      }
      else if (value === -2) {
        // non-working day
        return 0;
      }
      
      if (this.staffAlloc === 'headcount') {
        return `${Math.ceil(parseFloat(headcount).toFixed(1).replace(/\.?0+$/, ''))} ${this.resourceField === 'staffUuid' ? this.$t('staff.group.staff') : this.$t('resource.units')}`;
      }
      else if (this.staffAlloc === 'hours') {
        return value % 1 !== 0 ? `${parseFloat(value).toFixed(4).replace(/\.?0+$/, '')}h` : `${parseFloat(value).toFixed(4).replace(/\.?0+$/, '')}h`;
      }
      else {
        return value % 1 !== 0 ? `${parseFloat(value / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D` : `${parseFloat(value / 8.0).toFixed(4).replace(/\.?0+$/, '')}D`;
      }
    },
    eventAdd(e) {
      // don't let keynav handle this event
      e.stopPropagation();
      e.preventDefault();
      this.$emit('addEvent');
    },
    eventCopy() {
      /* eslint-disable no-undef */
      const toCopy = this.selected.map(i => { 
        const ev = cloneDeep(this.schedulerInstance.getEvent(i));
        ev.refId = ev.id;
        ev.id = null;
        return ev;
      });
      /* eslint-enable no-undef */
      this.copied = true;
      this.$emit('copyEvent', toCopy);
    },
    eventPaste() {
      this.$emit('pasteEvent');
    },
    eventEdit() {
      /* eslint-disable no-undef */
      const toEdit = this.selected.map(i => { 
        const event = this.schedulerInstance.getEvent(i);
        return { uuId: i, name: event.text, type: event.type, pu: event.pu ,
                 start_date: event.start_date, end_date: event.end_date,
                 calendar_type: event.calendar_type, readonly: event.readonly,
                 startHour: event.startHour, endHour: event.endHour, staffUuid: event.staffUuid
      }});

      this.$emit('editEvent', toEdit[0]);
    },
    // confirmDeleteEventOk() {
    //   const self = this;
    //   /* eslint-disable no-undef */
    //   const toRemove = this.selected.map(i => { 
    //     const event = this.schedulerInstance.getEvent(i);
        
    //     // Remove the usage data
    //     if (event.type === 'task' ||
    //         event.type === 'booking' ||
    //         event.type === 'activity') {
    //       const staff = this.treeData.find(s => s.uuId === event.staffUuid);
    //       if (staff) {
    //         const task = staff[`${event.type}List`].findIndex(t => t.uuId === event.id);
    //         if (task !== -1) {
    //           staff[`${event.type}List`].splice(task, 1);
    //         }
    //       }
    //     }
        
    //     const uuId = i.slice(i.length - 36);
        
    //     if (uuId in self.markedSpans) {
    //       for (const span of self.markedSpans[uuId]) {
    //         this.schedulerInstance.deleteMarkedTimespan(span.id);
    //       }
    //       delete self.markedSpans[uuId];
    //     }
    //     return { uuId: uuId, type: event.type, section_id: event.section_id }
    //   });
      
    //   for (const r of this.selected) {
    //     const ev = this.schedulerInstance.getEvent(r);
    //     if (ev && ev.clones) {
    //       // remove clones of the event
    //       for (const rClone of ev.clones) {
              
    //         this.schedulerInstance.deleteEvent(rClone);
    //       }
    //     }
    //     this.schedulerInstance.deleteEvent(r);
    //     /* eslint-enable no-undef */
    //   } 
    //   this.$emit('deleteEvent', toRemove);
    // },
    processTreeNodes(treeData) {
      for (const data of treeData) {
        if (data.path.length <= this.expandLevel) {
          data.open = true;
        }
        else {
          data.open = false;
        }
        
        this.$emit('folderToggle', { section: data, isOpen: data.open });
        if (data.children) {
          this.processTreeNodes(data.children);
        }
      }
    },
    combineTasks(list, tasks, tmap) {
      if (!tmap) {
        tmap = {};
      }
      
      for (const task of tasks) {
        if (!(task.uuId in tmap)) {
          tmap[task.uuId] = task;
          list.push(task);
        }
      }
      return { t: list, tmap: tmap };
    },
    applyFilter(obj, type) {
      if (this.filterObject.length !== 0 &&
          this.filter.displayFilteredMetrics &&
          this.hasEventFilters()) {
        for (const f of this.filterObject) {
          if ((f.field === 'projectName' &&
              (type === 'task' ||
               type === 'booking')) ||
              f.field === 'activityName' ||
              f.field === 'bookingName' ||
              f.field === 'taskName') {
            const field = f.field === 'projectName' ? 'pn' : 'name';
            if (!f.operator || f.operator === 'is') {
              if (-1 !== f.value.findIndex(v => obj[field] === v.text)) {
                return true;
              }
            }
            else {
              if (f.value.length === f.value.filter(v => obj[field] !== v.text).length) {
                return true;
              }            
            }
          }
          else if ((f.field === 'stageName' &&
              (type === 'task' ||
               type === 'booking') ||
               type === 'activity')) {
            const field = 'stageName';
            
            // for the 'is' operator we use or conditions, for 'is_not' we use and condtions
            if (!f.operator ||
                f.operator === 'is') {
              if (-1 !== f.value.findIndex(v => obj[field] === v.text)) {
                return true;
              }
            }
            else {
              if (f.value.length === f.value.filter(v => obj[field] !== v.text).length) {
                return true;
              }
            }
          }
        }
        return false;
      }
      return true;
    },
    getWorkList(section, projectId) {
      const ret = [];
      if (projectId === "000000000000-0000-0000-0000-00000000") {
        if (this.filter.usageTasks && section.taskList) {
          if (this.filterByProject) {
            ret.push(...section.taskList.filter(t => section.key.includes(t.project)));
          }
          else {
            ret.push(...section.taskList.filter(t => this.applyFilter(t, 'task')));
          }
        }
        if (this.filter.usageActivities && section.activityList) {
          ret.push(...section.activityList.filter(t => this.applyFilter(t, 'activity')));
        }
        if (this.filter.usageBookings && section.bookingList) {
          if (this.filterByProject) {
            ret.push(...section.bookingList.filter(b => section.key.includes(b.project)));
          }
          else {
            ret.push(...section.bookingList.filter(t => this.applyFilter(t, 'booking')));
          }
        }
      }
      else {
        if (this.filter.usageTasks && section.taskList) {
          ret.push(...section.taskList.filter(t => t.project === projectId).filter(t => this.applyFilter(t, 'task')));
        }
        if (this.filter.usageActivities && section.activityList) {
          ret.push(...section.activityList.filter(t => this.applyFilter(t, 'activity')));
        }
        if (this.filter.usageBookings && section.bookingList) {
          ret.push(...section.bookingList.filter(t => t.project === projectId).filter(t => this.applyFilter(t, 'booking')));
        }
      }
      
      if (!this.filter.singleContracts && section.duplicateStaff) {
        for (const s of section.duplicateStaff) {
          // use the key of the shown staff section
          ret.push(...this.getWorkList({ key: section.key, ...s}, projectId));
        }
      }         
      return ret;
    },
    calculateUsage(date, section, projectId, genericAvailability = true) {
      const children = this.filter.displayFilteredMetrics ? section.children : section.unfilteredChildren;
      
      if ((section.type === 'staff' && this.resourceField === 'staffUuid') ||
          (section.type === 'resource' && this.resourceField === 'resourceUuid')) {
        // We need UTC dates for the usage
        const utcDate = Date.UTC(date.getFullYear(), date.getMonth(),
                  date.getDate(), date.getHours(),
                  date.getMinutes(), date.getSeconds());
        const d = moment(utcDate).utc();
        
        let w = {};
        if (`${section.key}${date.getTime()}` in usageCache) {
          w = usageCache[`${section.key}${date.getTime()}`];
        }
        else {
          const resolvedDates = this.resolveStaffStartAndEnd(section, d.unix() * 1000);
          let begin = resolvedDates.begin;
          let until = resolvedDates.until;
          w = getUsage(!this.filter.usageNonWork ? section.calendars : [section.calendars[2]], 
                             d, 
                             this.span, 
                             this.getWorkList(section, projectId),
                             begin,
                             until,
                             section.quota,
                             this.staffAlloc,
                             section.perhourCost,
                             moment(this.startDate),
                             moment(this.endDate));
          
          w.capacityCost = w.a * section.perhourCost;
          w.code = section.currency;
          if (this.staffAlloc !== 'fixedcost') {
            usageCache[`${section.key}${date.getTime()}`] = w;
          }
        }
        
        if (!genericAvailability && section.generic) {
          w = cloneDeep(w); // we are changing the value, clone so we do not change the cached value
          w.a = 0; // do not use generic availability for grouping availability
          w.basecost = 0;
        }    
        
        // if staff metrics are shown clear the usage data
        if (this.filter.staff_metrics &&
            this.resourceField === 'resourceUuid') {
          w = { a: 0, w: 0, wFixed: 0, wActual: 0, h: 0, ha: 0, t: [], tmap: {}, cost: 0, costNet: 0, code: '', basecost: 0, capacityCost: 0  };
        }
        
        if (section.type === 'resource' && this.resourceField === 'resourceUuid' &&
            children && this.filter.staff_metrics) {
          // For resources we want the usage of staff assigned to this resource
          // The headcount availability is the quote of the resource
          w.ha = 1;
          w.a = getDefaultAvailability(this.span, this.durationConversionOpts);
          
          for (const child of children) {
            const result = this.calculateUsage(date, child, "000000000000-0000-0000-0000-00000000", false);
            w.w += result.w;
            w.wFixed += result.wFixed;
            w.wActual += result.wActual;
            w.h += result.h;
            const combineResult = this.combineTasks(w.t, result.t, w.tmap);
            w.t = combineResult.t;
            w.tmap = combineResult.tmap;

            const resultCost = getCost(w.t, section.perhourCost, this.staffAlloc);
            w.basecost = w.a * section.perhourCost;
            w.cost = resultCost.cost;
            w.costNet = resultCost.netCost;
            w.fixedCost = resultCost.fixedCost;
            w.fixedCostNet = resultCost.netFixedCost;
            w.actualCost = resultCost.actualCost;
            w.actualCostNet = resultCost.netActualCost;
            w.capacityCost = w.a * section.perhourCost;
            
          }
        }          
        return w;
      }
      else if (section.type === 'staff' && this.resourceField === 'resourceUuid') {
        // We need UTC dates for the usage
        const utcDate = Date.UTC(date.getFullYear(), date.getMonth(),
                  date.getDate(), date.getHours(),
                  date.getMinutes(), date.getSeconds());
        const d = moment(utcDate).utc();
        
        let rw = {};
        
        // each resource assigned to a staff is used, multiple by quota
        const quota = section.quota ? section.quota : 1;
        const wh = getDefaultAvailability(this.span, this.durationConversionOpts) * quota;
        rw = getUsage(section.calendars, 
                             d, 
                             this.span, 
                             this.getWorkList(section, projectId),
                             section.begin,
                             section.until,
                             section.quota,
                             this.staffAlloc,
                             section.perhourCost,
                             moment(this.startDate),
                             moment(this.endDate));
        return { a: 0, w: rw.a === 0 ? 0 : wh, wFixed: 0, wActual: 0, h: rw.a === 0 ? 0 : 1 * quota, ha: 1, t: [], cost: this.filter.staff_metrics ? wh * section.perhourCost : 0, costNet: this.filter.staff_metrics ? wh * section.perhourCost : 0, code: section.currency, basecost: 0, defaultAvailability: wh, capacityCost: 0 };
      }
      else if (children) {
        const w1 = { a: 0, w: 0, wFixed: 0, wActual: 0, h: 0, ha: 0, t: [], tmap: {}, cost: 0, costNet: 0, fixedCost: 0, fixedCostNet: 0, actualCost: 0, actualCostNet: 0, code: '', basecost: 0, defaultAvailability: 0, capacityCost: 0 };
        for (const child1 of children) {
          // only add generic usage to project groups, if the section is not a project then we don't count generic
          const result1 = this.calculateUsage(date, child1, "000000000000-0000-0000-0000-00000000", !this.showStaff);
          const quota = child1.quota ? child1.quota : 1;
          if (result1.a !== -1) {
            w1.a += result1.a * quota;  // increase the availability based upon the quota
          }
          w1.w += result1.w;
          w1.wFixed += result1.wFixed;
          w1.wActual += result1.wActual;
          w1.h += result1.h;
          w1.ha += result1.ha * quota;
          const combineResult = this.combineTasks(w1.t, result1.t, w1.tmap);
          w1.t = combineResult.t;
          w1.tmap = combineResult.tmap;
          w1.basecost += result1.a * child1.perhourCost;
          if (result1.cost) {
            w1.cost += result1.cost;
          }
          if (result1.costNet) {
            w1.costNet += result1.costNet;
          }
          if (result1.fixedCost) {
            w1.fixedCost += result1.fixedCost;
          }
          if (result1.fixedCostNet) {
            w1.fixedCostNet += result1.fixedCostNet;
          }
          if (result1.actualCost) {
            w1.actualCost += result1.actualCost;
          }
          if (result1.actualCostNet) {
            w1.actualCostNet += result1.actualCostNet;
          }
          if (result1.capacityCost) {
            w1.capacityCost += result1.capacityCost;
          }
          w1.defaultAvailability += result1.defaultAvailability * quota;
          w1.code = child1.currency;
        }
        
        if (this.resourceField !== 'resourceUuid') {
          // calculate the availability for all children of this group
          const allChildren = this.filter.displayFilteredMetrics ? section.children : section.allChildren;
          w1.a = 0;
          w1.ha = 0;
          for (const child2 of allChildren) {
            // only add generic usage to project groups, if the section is not a project then we don't count generic
            const result1 = this.calculateUsage(date, child2, "000000000000-0000-0000-0000-00000000", !this.showStaff);
            const quota = child2.quota ? child2.quota : 1;
            if (result1.a !== -1) {
              w1.a += result1.a * quota;  // increase the availability based upon the quota
            }
            w1.ha += result1.ha * quota;
          }
        }        
        return w1;
      }
      return { a: 0, w: 0, wFixed: 0, wActual: 0, h: 0, ha: 0, t: [], defaultAvailability: 0 };
    },
    getDefaultColor(event) {
      if (event.type === 'task') {
        return 'var(--gantt-task)';
      }
      else if (event.type === 'activity') {
        return 'var(--activity)';
      }
      else if (event.type === 'booking') {
        return 'var(--booking)';
      }
      else if (event.type === 'vacation') {
        if (event.readonly) {
          return 'var(--vacation-readonly)';
        }
        return 'var(--vacation)';
      }
      else if (event.type === 'resourceUnit') {
        return '#8bc34a99';
      }
      return 'var(--surface-bg)';
    },
    getProgressStyle(event) {
      const zIndex = event.readonly || this.lockPlanner ? 'z-index: 2;' : ''; // we need to hide the resize drag elements
      const eventColor = this.filter.fullBarColor && event.dotcolor ? event.dotcolor : this.getDefaultColor(event);
      const textColor = this.filter.fullBarColor && event.dotcolor && !event.dotcolor.startsWith('var(') ? invertColor(this.filter.fullBarColor && event.dotcolor, true) : eventColor === 'var(--surface-bg)' ? 'var(--text-medium)' : 'var(--white)';
      if (typeof event.tp === 'number') {
        const progressColor = !this.filter.fullBarColor || !event.dotcolor || event.dotcolor.startsWith('var') ? 'var(--gantt-task-progress)' : toComplimentary(event.dotcolor);
        return `${zIndex} position: relative; left: -10px; margin-right: -10px; background: linear-gradient(90deg, ${progressColor} ${event.tp * 100}%, ${eventColor} 0%); color: ${textColor}`;
      }
      return `${zIndex} position: relative; left: -10px; margin-right: -10px; background: ${eventColor}; color: ${textColor}`;
    },
    hasEventFilters() {
      let ret = false;
      for (const f of this.filterObject) {
        if (f.field === 'projectName' ||
            f.field === 'activityName' ||
            f.field === 'bookingName' ||
            f.field === 'taskName' ||
            f.field === 'nonWork' ||
            f.field === 'stageName') {
          ret = true;
          break;
        }
      }
      return ret;
    },
    shouldAdd(event) {
      if (!event.section_id) {
        return false;
      }
      
      if (this.filter.showOnlyWarnings) {
        let bookingWarn = this.filter.alertTaskBookings && event.type === 'task' && !event.booking ? true : false;
        let booking_overlap = event.type === 'booking' && this.filter.alertBookings && event.type === 'booking' && event.hasOverlap ? true : false;
        let task_overlap = event.type === 'task' && this.filter.alertTasks && event.hasOverlap ? true : false;
        let activity_overlap = event.type === 'activity' && this.filter.alertActivities && event.hasOverlap ? true : false;
        let nonwork_overlap = event.type === 'vacation' && this.filter.alertNonWork && event.hasOverlap ? true : false;
        
        if (!(bookingWarn ||
             nonwork_overlap ||
             activity_overlap ||
             task_overlap ||
             booking_overlap) &&
            (this.draggingEvent === null || event.id !== this.draggingEvent.id)) {
          return false;
        }
      }
      
      if (typeof event.id === 'number' ||
          (this.draggingEvent !== null && event.id === this.draggingEvent.id)) {
        return true;
      }
      
      let ret = true;
      let projectId = this.projectId;
      if (this.filterByProject) {
        projectId = event.section_id;
      }
                            
      // commented out filtering events based upon filter
      if (this.filterObject.length !== 0) {
        if (this.filter.displayFilteredMetrics && this.hasEventFilters()) {
          // hide by default
          ret = false;
        }
        
        let filterCount = 0;
        let matchCount = 0;
        for (const f of this.filterObject) {
          if ((f.field === 'projectName' &&
              (event.type === 'task' ||
               event.type === 'booking')) ||
              f.field === 'activityName' ||
              f.field === 'bookingName' ||
              f.field === 'taskName' ||
              f.field === 'nonWork') {
            filterCount++;
            const field = f.field === 'projectName' ? 'pn' : f.field === 'taskName' ? 'name' : 'text';
            
            // for the 'is' operator we use or conditions, for 'is_not' we use and condtions
            if (!f.operator ||
                f.operator === 'is') {
              if (-1 !== f.value.findIndex(v => event[field] === v.text)) {
                ret = true;
                matchCount++;
              }
              else {
                ret = false;
              }
            }
            else {
              if (f.value.length === f.value.filter(v => event[field] !== v.text).length) {
                ret = true;
                matchCount++;
              }
              else {
                ret = false;
              }
            }
          }
          else if (f.field === 'stageName' &&
              (event.type === 'task' ||
               event.type === 'booking' ||
               event.type === 'activity')) {
            filterCount++;
            const field = 'stageName';
            
            // for the 'is' operator we use or conditions, for 'is_not' we use and condtions
            if (!f.operator ||
                f.operator === 'is') {
              if (-1 !== f.value.findIndex(v => event[field] === v.text)) {
                ret = true;
                matchCount++;
              }
              else {
                ret = false;
              }
            }
            else {
              if (f.value.length === f.value.filter(v => event[field] !== v.text).length) {
                ret = true;
                matchCount++;
              }
              else {
                ret = false;
              }
            }
          }
        }
        
        // make sure it is AND
        if (matchCount !== filterCount) {
          ret = false;
        }
      }
      
      if (this.filter === null) {
        ret = true;
      }
      
      if (event.type === 'activity' &&
          typeof this.filter.activity !== 'undefined' &&
          !this.filter.activity) {
        ret = false;  
      }
      else if (event.type === 'booking' &&
          ((typeof this.filter.booking !== 'undefined' &&
          !this.filter.booking) ||
          (!projectId.includes(event.project) && projectId !== "000000000000-0000-0000-0000-00000000"))) {
        ret = false;  
      }
      else if (event.type === 'task' &&
          ((typeof this.filter.task !== 'undefined' &&
          !this.filter.task) ||
          (!projectId.includes(event.pu) && projectId !== "000000000000-0000-0000-0000-00000000"))) {
        ret = false;  
      }
      else if (event.type === 'vacation' &&
          typeof this.filter.vacation !== 'undefined' &&
          !this.filter.vacation) {
        ret = false;  
      }
      return ret;
    },
    mobileCheck() {
      let check = false;
      //eslint-disable-next-line
      (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
      return check;
    },
    updateMarkedTimespan(id, start, end /**, section_id */) {
      if (id in this.markedSpans) {
        for (const span of this.markedSpans[id]) {
          const timespanId = span.id;
          /* eslint-disable no-undef */
          this.schedulerInstance.deleteMarkedTimespan(timespanId);
          const newid = this.schedulerInstance.addMarkedTimespan({
                start_date: new Date(start),
                end_date: new Date(end),
                css: `load-marker-weekend id-${id}`,
                sections: {
                  timeline: span.key
                }
          });
          
          // update the calendar entries to match the change
          this.updateSectionCalendar(id, span.key, start, end);
          span.id = newid;
        }
        this.schedulerInstance.updateView();
        /* eslint-enable no-undef */
      }
    },
    updateTreeChildren(id, uuId, start, end) {
       for (const data of this.treeData) {
         const found = data.unfilteredChildren ? data.unfilteredChildren.find(c => c.uuId === uuId) : null;
         if (found) {
           const cal = found.calendars[0].find(c => c.uuId === id);
           if (cal) {
             cal.startDate = start;
             cal.endDate = end;
           }
         }
       }
    },
    updateSectionCalendar(id, key, start, end) {
      /* eslint-disable no-undef */
      const section = this.schedulerInstance.getSection(key);
      /* eslint-enable no-undef */
      if (section && section.calendars) {
        this.updateTreeChildren(id, section.uuId, start, end);
        const cal = section.calendars[0].find(c => c.uuId === id);
        if (cal) {
          cal.startDate = start;
          cal.endDate = end;
        }
      }
    },
    removeSectionCalendar(id, key) {
      /* eslint-disable no-undef */
      const section = this.schedulerInstance.getSection(key);
      /* eslint-enable no-undef */
      if (section && section.calendars) {
        const cal = section.calendars[0].findIndex(c => c.uuId === id);
        if (cal !== -1) {
          section.calendars[0].splice(cal, 1);
        }
      }
    },
    getSectionId(element) {
      if (element && element.hasAttribute('data-section-id')) {
        return element.getAttribute('data-section-id');
      }
      else if (element.parentElement) {
        return this.getSectionId(element.parentElement);
      }
      return null;
    },
    badAssign(begin, until, tasks) {
      for (const t of tasks) {
        if (t.begin < begin) {
          return true;
        }
        else if (t.until > until + 86400000) {
          return true;
        }
      }
      return false;
    },
    getEndDate(date) {
      if (this.span === 'day') {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
      }
      else if (this.span === 'week') {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 6, 23, 59, 59);
      }
      else if (this.span === 'month') {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
      }
      else if (this.span === 'year') {
        return new Date(date.getFullYear(), 11, 31);
      }
    },
    updateClones(clones, taskId, start, end) {
      for (const cloneId of clones) {
        /* eslint-disable no-undef */
        const cevent = this.schedulerInstance.getEvent(cloneId);
        if (cevent) {
          const section = this.schedulerInstance.getSection(cevent.section_id);
          /* eslint-enable no-undef */
          if (section) {
            const task = section.taskList ? section.taskList.find(t => t.uuId === taskId) : null;
            if (task) {
              task.begin = start;
              task.until = end;
            }
          }
        }
      }
    },
    disablePointer(event) {
      if (event) {
        if (event.type === 'resourceUnit' && !this.canEdit('STAFF')) {
          return true;
        }
      }
      return false;
    },
    isNonWorkingDay(date, section) {
      // We need UTC dates for the usage
      const utcDate = Date.UTC(date.getFullYear(), date.getMonth(),
                date.getDate(), date.getHours(),
                date.getMinutes(), date.getSeconds());
      const d = moment(utcDate).utc();
      if (section.calendars) {
        for (const calendar of section.calendars) {
          if (calendar != null) {
            for (const c of calendar) {
              if (c.v_CalendarType && c.v_CalendarType[0] === date.getDay() + 1) {
                // there may be multiple entries for this day
                if (!c.v_EndHour && !c.v_StartHour) {
                  const utc = moment(Date.UTC(date.getFullYear(), date.getMonth(),
                  date.getDate(), date.getHours(),
                  date.getMinutes(), date.getSeconds())).unix() * 1000;
                  const filt = calendar.filter(cf => cf.type === 'Working' && cf.startDate <= utc && utc <= cf.endDate);
                  if (filt.length > 0) {
                    return false; // we have a working exception
                  }
                  return true;
                }
                else {
                  return false;
                }
              }
              else if (d.unix() * 1000 >= c.startDate && d.unix() * 1000 <= c.endDate) {
                // exception overrides the location or base calendar
                if (c.type === 'Working') {
                  return false;
                }
              }
            }
          }
        }
      }
      else {
        if (date.getDay() === 0 || date.getDay() === 6) {
          return true;
        }
      }
      return false;
    },
    formatDateForExport(date, useTime) {
      const m = moment(date);
      if (m.year() === 3000 ||
          m.year() === 1970) {
        return '';   
      }
      if (useTime) {
        return m.format("YYYY-MM-DD HH:mm:ss")
      }
      return m.format("YYYY-MM-DD")
    },
    getEntityDataIds(data, idsMap) {
      const cmdList = [];
      const ids = [];
      const genericIds = [];
      const companyIds = [];
      const locationIds = [];
      const stageIds = [];
      const projectIds = [];
      const departmentIds = [];
      const skillIds = [];
      
      // loop through and get all the activities/bookings/tasks first
      for (const d of data) {
        // add the activities, bookings, tasks and nonwork
        if (d.type === 'staff' ||
            d.type === 'resource') {
          if (!(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            if (d.generic) {
              genericIds.push(d.uuId);
            }
            else {
              ids.push(d.uuId);
            }
              
            const label = d.type === 'staff' ? 'Staff' : 'Resource';
            if (d.activityList) {
              for (const activity of d.activityList) {
                if (!(activity.uuId in idsMap)) {
                  idsMap[activity.uuId] = true;
                  cmdList.push({
                    method: 'POST',
                    invoke: '/api/activity/get',
                    body: [ { uuId: activity.uuId }],
                    vars: [],
                    note: 'activity_update__get'
                  })
                }
              }
            }
            
            if (d.bookingList) {
              for (const booking of d.bookingList) {
                if (!(booking.uuId in idsMap)) {
                  idsMap[booking.uuId] = true;
                  cmdList.push({
                    method: 'POST',
                    invoke: '/api/booking/get',
                    body: [ { uuId: booking.uuId }],
                    vars: [],
                    note: 'booking_update__get'
                  })
                }
              }
            }
            
            if (d.taskList) {
              for (const task of d.taskList) {
                if (!(task.uuId in idsMap)) {
                  idsMap[task.uuId] = true;
                  cmdList.push({
                    method: 'POST',
                    invoke: '/api/task/get',
                    body: [{ uuId: task.uuId }],
                    vars: [],
                    note: 'task_update__get'
                  })
                }
              }
            }
          }
        }
        else if (d.type === 'company') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            companyIds.push(d.uuId);
          }    
        }
        else if (d.type === 'location') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            locationIds.push(d.uuId);
          }    
        }
        else if (d.type === 'stage') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            stageIds.push(d.uuId);
          }    
        }
        else if (d.type === 'project') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            projectIds.push(d.uuId);
          }    
        }
        else if (d.type === 'department') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            departmentIds.push(d.uuId);
          }    
        }
        else if (d.type === 'skill') {
          if (UUID_CHECK.test(d.uuId) && !(d.uuId in idsMap)) {
            idsMap[d.uuId] = true;
            skillIds.push(d.uuId);
          }    
        }
        
        if (d.children) {
          const result = this.getEntityDataIds(d.children, idsMap);
          cmdList.push(...result.cmdList);
          ids.push(...result.ids);
          companyIds.push(...result.companyIds);
          locationIds.push(...result.locationIds);
          stageIds.push(...result.stageIds);
          projectIds.push(...result.projectIds);
          departmentIds.push(...result.departmentIds);
          skillIds.push(...result.skillIds);
          genericIds.push(...result.genericIds);
        }
      }
      return { cmdList, ids, genericIds, companyIds, locationIds, stageIds, projectIds, departmentIds, skillIds };
    },
    async getEntityData(data) {
      const self = this;
      const cmdList = [];
      let entities = {};
      const ids = [];
      const genericIds = [];
      const companyIds = [];
      const locationIds = [];
      const stageIds = [];
      const projectIds = [];
      const departmentIds = [];
      const skillIds = [];
      
      const result = this.getEntityDataIds(data, {});
      cmdList.push(...result.cmdList);
      ids.push(...result.ids);
      genericIds.push(...result.genericIds);
      companyIds.push(...result.companyIds);
      locationIds.push(...result.locationIds);
      stageIds.push(...result.stageIds);
      projectIds.push(...result.projectIds);
      departmentIds.push(...result.departmentIds);
      skillIds.push(...result.skillIds);
      
      await getCustomFieldInfo(this, 'BOOKING', null, { customFieldsPropName: 'bookingCustomFields' }).catch(e => this.httpAjaxError(e));
      await getCustomFieldInfo(this, 'ACTIVITY', null, { customFieldsPropName: 'activityCustomFields' }).catch(e => this.httpAjaxError(e));
      await getCustomFieldInfo(this, 'TASK', null, { customFieldsPropName: 'taskCustomFields' }).catch(e => this.httpAjaxError(e));
      
      if (cmdList.length > 0) {
        for (var i = 0; i < cmdList.length; i += 500) {
          const list = cmdList.slice(i, i + 500);
          await compositeService.exec(list, true)
          .then((response) => {
            for (const res of response.data.feedbackList) {
              entities[res.fetch[0].uuId] = res.fetch[0];
            }
          })
          .catch(() => {
            this.alertMsg = this.$t('vacation.import_error');
            return [];
          });
        }
      }
      
      // get the data for the staff fields
      const requests = [];
      if (ids.length > 0 && this.resourceField === 'staffUuid') {
        for (var stf = 0; stf < ids.length; stf += 200) {
          const list = ids.slice(stf, stf+200);
          requests.push(staffService.listv2({ 
            start: 0
            , limit: -1
            , holders: list
            , fields: JSON.parse(JSON.stringify(this.getFields()))
          }, false, this.customFields));
        }
      }
      if (ids.length > 0 && this.resourceField === 'resourceUuid') {
        for (var res = 0; res < ids.length; res += 200) {
          const rlist = ids.slice(res, res+200);
          requests.push(resourceService.list({ 
            start: 0
            , limit: -1
            , holders: ids
            , fields: JSON.parse(JSON.stringify(this.getFields()))
          }, false, this.customFields));
        }
      }
      if (genericIds.length > 0) {
        requests.push(staffService.listv2({ 
          start: 0
          , limit: -1
          , holders: genericIds
          , fields: JSON.parse(JSON.stringify(this.getFields()))
        }, true, this.customFields));
      }
      
      if (companyIds.length > 0) {
        await companyService.listv2({ 
          start: 0
          , limit: -1
          , holders: companyIds
          , fields: JSON.parse(JSON.stringify(this.companyFields))
        }, this.companyCustomFields).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'company', this.companyFields), entities, this.getFields(), this.companyCustomFields);
        });
      }

      if (locationIds.length > 0) {
        await locationService.list({ 
          start: 0
          , limit: -1
          , holder: locationIds
          , fields: JSON.parse(JSON.stringify(['countryCode', ...this.locationFields])) // countryCode is required to get countryName
        }, true, this.locationCustomFields).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'location', this.locationFields), entities, this.getFields(), this.locationCustomFields);
        });
      }

      if (stageIds.length > 0) {
        await stageService.list({ 
          start: 0
          , limit: -1
          , holder: stageIds
          , fields: JSON.parse(JSON.stringify(this.stageFields))
        }, this.stageCustomFields).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'stage', this.stageFields), entities, this.getFields(), this.stageCustomFields);
        });
      }

      if (projectIds.length > 0) {
        await projectService.listv2({ 
          start: 0
          , limit: -1
          , holders: projectIds
          , fields: JSON.parse(JSON.stringify(this.projectFields))
        }, this.projectCustomFields).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'project', this.projectFields), entities, this.getFields(), this.projectCustomFields);
        });
      }

      if (departmentIds.length > 0) {
        await departmentService.list({ 
          start: 0
          , limit: -1
          , holders: departmentIds
          , fields: JSON.parse(JSON.stringify(this.departmentFields))
        }, this.departmentCustomFields).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'department', this.departmentFields), entities, this.getFields(), this.departmentCustomFields);
        });
      }

      if (skillIds.length > 0) {
        await skillService.list({ 
          start: 0
          , limit: -1
          , holders: skillIds
          , fields: JSON.parse(JSON.stringify(this.skillFields))
        }, null, { customFields: this.skillCustomFields }).then(response => {
          entities = self.processEntityData(this.processResponseData(response.data, 'skill', this.skillFields), entities, this.getFields(), this.skillCustomFields);
        });
      }
      
      await Promise.allSettled(requests)
      .then(responses => {
        const filteredResponseList = responses.filter(i => i.status == 'fulfilled');
        for (const r of filteredResponseList) {
          const data = r.value?.data != null && Array.isArray(r.value.data)? r.value.data : [];
          entities = self.processEntityData(data, entities, this.getFields(), this.customFields);
        }
      });
      return entities;
    },
    getFields() {
      const fields = [];
      for (const field of this.fields) {
        if (field === 'quota') {
          fields.push('resourceQuota');
        }
        else {
          fields.push(field);
        }
      }
      return fields;
    },
    getFieldName(group, field) {
      return `${group}${String(field).charAt(0).toUpperCase() + String(field).slice(1)}`;
    },
    processResponseData(data, group, fields) {
      for (const row of data) {
        for (const field of fields) {
          row[this.getFieldName(group, field)] = row[field]; // transforming the field name to match the column fields
        }
      }
      return data;
    },
    processEntityData(data, entities, fields, customFields, prefix = null) {
      for (const row of data) {
        const entityData = {};
        for (const field of fields) {
          const customField = customFields.find(cf => cf.name === field);

          if (field.endsWith('tartDate') ||
              field.endsWith('ndDate') ||
              field.endsWith('cheduleStart') ||
              field.endsWith('cheduleFinish') ||
              (customField && customField.type === 'Date')) {
            if (typeof row[field] !== 'undefined' &&
                !Array.isArray(row[field]) &&
                row[field] !== 0 &&
                row[field] !== 32503680000000) {
              entityData[field] = moment(row[field]).format('L');
            }
            else {
              entityData[field] = '';
            }
          }
          else if (field === 'companyType') {
            entityData[field] = row['type'];
          }
          else if (field === 'tag') {
            entityData[field] = typeof row[field] !== 'undefined' ? row[field].join(', ') : '';
          }
          else if (Array.isArray(row[field])) {
            entityData[field] = row[field].map(r => r.name).join(', ');
          }
          else {
            entityData[field] = row[field];
          }
        }
        
        if (entities[row.uuId]) {
          // copy existing keys
          for (const key of Object.keys(entities[row.uuId])) {
            entityData[key] = entities[row.uuId][key];
          }
        }
        entities[row.uuId] = entityData;
      }
      return entities;
    },
    async getRows(result, data, groupings, entities) {
      const self = this;
      
      for (const d of data) {
        // add the activities, bookings, tasks and nonwork
        if ((d.type === 'staff' ||
            d.type === 'resource') &&
            !(d.uuId in result.added)) {
          result.added[d.uuId] = true;
          const label = d.type === 'staff' ? 'Staff' : 'Resource';
          if (d.activityList) {
            for (const activity of d.activityList) {
              const act = {};
              const actGet = entities[activity.uuId] ? entities[activity.uuId] : await activityService.get([{ uuId: activity.uuId }], ['NOTE']).then((response) => {
                return response.data.objectList[0];
              }).catch(e => {
                //eslint-disable-next-line
                console.log(e);
              });
              
              act["Name"] = activity.name;
              act["Start Date"] = this.formatDateForExport(activity.begin, true);
              act["End Date"] = this.formatDateForExport(activity.until, true);
              act["Estimated Duration"] = `${activity.duration / 60000 / 60 / this.durationConversionOpts.hourPerDay}D`;
              act[label] = activity.utilization === 1 ? d.name : `${d.name} (${activity.utilization * 100}%)`;
              act["Stage"] = activity.stageName;
              act["Description"] = actGet.description;
              act["Note"] = actGet.noteList ? actGet.noteList.map(n => {return n.text}) : '';
              act["Estimated Cost"] = `${this.getCurrencySymbol(actGet.currencyCode ? actGet.currencyCode : 'USD')}${actGet.estimatedCost}`;
              act["Fixed Cost"] = `${this.getCurrencySymbol(actGet.currencyCode ? actGet.currencyCode : 'USD')}${typeof actGet.fixedCost !== 'undefined' ? actGet.fixedCost : 0}`;
              act["Currency"] = actGet.currencyCode;
              act["Fixed Duration"] = actGet.fixedDuration ? `${actGet.fixedDuration / 60 / this.durationConversionOpts.hourPerDay}D` : '0D';
              act["Actual Duration"] = actGet.actualDuration ? `${actGet.actualDuration / 60 / this.durationConversionOpts.hourPerDay}D` : '0D';
              for (const cf of this.activityCustomFields) {
                act[cf.displayName] = actGet[cf.name];
              }
              result.activities.push(act);
            }
          }
          if (d.bookingList) {
            for (const booking of d.bookingList) {
              const book = {};
              const bookGet = entities[booking.uuId] ? entities[booking.uuId] : await bookingService.get([{ uuId: booking.uuId }], ['NOTE']).then((response) => {
                return response.data.objectList[0];
              }).catch(e => {
                //eslint-disable-next-line
                console.log(e);
              });
              
              book["Name"] = booking.name;
              book["Project"] = booking.pn;
              book["Start Date"] = this.formatDateForExport(booking.begin, true);
              book["End Date"] = this.formatDateForExport(booking.until, true); 
              book["Estimated Duration"] = `${(typeof booking.duration !== 'undefined' ? booking.duration : booking.te) / 60000 / 60 / this.durationConversionOpts.hourPerDay}D`;
              book[label] = booking.utilization === 1 ? d.name : `${d.name} (${booking.utilization * 100}%)`;
              book["Stage"] = booking.stageName;
              book["Description"] = bookGet.description;
              book["Note"] = bookGet.noteList ? bookGet.noteList.map(n => {return n.text}) : '';
              book["Estimated Cost"] = `${this.getCurrencySymbol(bookGet.currencyCode ? bookGet.currencyCode : 'USD')}${bookGet.estimatedCost}`;
              book["Fixed Cost"] = `${this.getCurrencySymbol(bookGet.currencyCode ? bookGet.currencyCode : 'USD')}${typeof bookGet.fixedCost !== 'undefined' ? bookGet.fixedCost : 0}`;
              book["Currency"] = bookGet.currencyCode;
              book["Fixed Duration"] = bookGet.fixedDuration ? `${bookGet.fixedDuration / 60 / this.durationConversionOpts.hourPerDay}D` : '0D';
              for (const cf of this.bookingCustomFields) {
                book[cf.displayName] = bookGet[cf.name];
              }
              result.bookings.push(book);
            }
          }
          if (d.taskList) {
            for (const task of d.taskList) {
              const ta = {};
              const taskGet = entities[task.uuId] ? entities[task.uuId] : await taskService.get([{ uuId: task.uuId }], ['NOTE']).then((response) => {
                return response.data.objectList[0];
              }).catch(e => {
                //eslint-disable-next-line
                console.log(e);
              });
              
              ta["Name"] = task.name;
              ta["Project"] = task.pn;
              ta["Start Date"] = this.formatDateForExport(task.begin, true);
              ta["End Date"] = this.formatDateForExport(task.until, true);
              ta["Estimated Duration"] = `${task.te / 60000 / 60 / this.durationConversionOpts.hourPerDay}D`;
              ta[label] = ta[label] = task.utilization === 1 ? d.name : `${d.name} (${task.utilization * 100}%)`;
              ta["Progress (%)"] = task.tp * 100;
              ta["Stage"] = task.stageName;
              ta[this.$t('task.field.taskpath')] = task.path ? task.path.replace(/\n/g, ' / ') : task.name;
              ta["Description"] = taskGet.description;
              ta["Note"] = taskGet.noteList ? taskGet.noteList.map(n => {return n.text}) : '';
              ta["Estimated Cost"] = `${this.getCurrencySymbol(taskGet.currencyCode ? taskGet.currencyCode : 'USD')}${taskGet.estimatedCost}`;
              ta["Fixed Cost"] = `${this.getCurrencySymbol(taskGet.currencyCode ? taskGet.currencyCode : 'USD')}${typeof taskGet.fixedCost !== 'undefined' ? taskGet.fixedCost : 0}`;
              ta["Currency"] = taskGet.currencyCode;
              ta["Fixed Duration"] = taskGet.fixedDuration ? `${taskGet.fixedDuration / 60 / this.durationConversionOpts.hourPerDay}D` : '0D';
              for (const cf of this.taskCustomFields) {
                ta[cf.displayName] = taskGet[cf.name];
              }
              result.tasks.push(ta);
            }
          }
          if (d.calendarList) {
            for (const cal of d.calendarList) {
              if (cal.type === "Leave") {
                result.nonwork.push({ Staff: d.name, Name: cal.name, Type: "Non Working", "Start Date": this.formatDateForExport(cal.startDate, false), "End Date": this.formatDateForExport(cal.endDate, false), "Start Time": "", "End Time": "" });
              }
              else if (cal.type === "Working") {
                result.nonwork.push({ Staff: d.name, Name: cal.name, Type: "Working", "Start Date": this.formatDateForExport(cal.startDate, false), "End Date": this.formatDateForExport(cal.endDate, false), "Start Time": msToTime(cal.startHour), "End Time": msToTime(cal.endHour) });
              }
            }
          }
          if (d.calendars) {
            if (d.calendars.length === 3) { // 3 calendars is personal, location and base
              for (const loccal of d.calendars[1]) { // location calendar
                if (loccal.type === "Leave" &&
                    loccal.startDate > d.begin && loccal.startDate < d.until) {
                   result.holidays.push({ Staff: d.name, Name: loccal.name, "Start Date": this.formatDateForExport(loccal.startDate, false), "End Date": this.formatDateForExport(loccal.endDate, false) });
                }
              }
            }
          }
        }
        
        /* eslint-disable no-undef */
        var state = this.schedulerInstance.getState();
        /* eslint-enable no-undef */
        var from = new Date(state.min_date),
        to = new Date(state.max_date);
        /* eslint-disable no-undef */
        var timeline = this.schedulerInstance.matrix[state.mode];
        /* eslint-enable no-undef */
        const obj = {};

        if (d.type !== 'staff' &&
            d.type !== 'resource') {
          // clear out the saved structure when changing groups
          if (d.type === 'company') {
            delete groupings['location'];
            delete groupings['stage'];
            delete groupings['project'];
            delete groupings['department'];
            delete groupings['skill'];
          }
          else if (d.type === 'location') {
            delete groupings['stage'];
            delete groupings['project'];
            delete groupings['department'];
            delete groupings['skill'];
          }
          else if (d.type === 'stage') {
            delete groupings['project'];
            delete groupings['department'];
            delete groupings['skill'];
          }
          else if (d.type === 'project') {
            delete groupings['department'];
            delete groupings['skill'];
          }
          else if (d.type === 'department') {
            delete groupings['skill'];
          }
          
          groupings[d.type] = d.name;
        }
                   
        const totals = [
          'totalFixedDuration',
          'totalEstimatedDuration',
          'totalActualDuration',
          'totalFixedCost',
          'totalEstimatedCost',
          'totalActualCost'
        ];
        obj['Name'] = d.label;
        obj['Type'] = this.getExportLabel(d);
        if (typeof entities[d.uuId] !== 'undefined') {
          for (const key of Object.keys(entities[d.uuId])) {
            entities = this.copyToChildren(d, key, typeof d[key] !== 'undefined' ? d[key] : entities[d.uuId][key], entities);         
            // transform the key to remove any prefix
            const normalizedResult = this.normalizeKey(key);
            const normalizedKey = normalizedResult.key;
            const normalizedGroup = normalizedResult.group ? normalizedResult.group : 'staff';
            
            const customField = this.customFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.companyCustomFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.locationCustomFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.stageCustomFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.projectCustomFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.departmentCustomFields.find(cf => cf.name === normalizedKey || cf.name === key) ||
                                this.skillCustomFields.find(cf => cf.name === normalizedKey || cf.name === key);
                       
            if (customField) {
              const groupName = this.$t(`staff.group.${normalizedGroup}`);
              const label = normalizedGroup ? `${groupName} ${customField.displayName}` : customField.displayName;
              obj[label] = typeof d[key] !== 'undefined' ? d[key] : entities[d.uuId][key];
            }
            else if (normalizedGroup === 'company' ||
                     normalizedGroup === 'location' ||
                     normalizedGroup === 'stage' ||
                     normalizedGroup === 'project' ||
                     normalizedGroup === 'department' ||
                     normalizedGroup === 'skill') {
              const label = normalizedKey === 'identifier' ? this.$t('field.identifier_full') : this.$t(`${normalizedGroup}.field.${this.transformKey(normalizedKey)}`);
              const groupLabel = this.$t(`staff.group.${normalizedGroup}`);
              const fieldLabel = `${groupLabel} ${label}`
              obj[fieldLabel] = typeof d[key] !== 'undefined' ? d[key] : entities[d.uuId][key];
            }
            else {
              const label = key === 'identifier' ? this.$t('field.identifier_full') : this.$t(`staff.field.${this.transformKey(key)}`);
              const groupLabel = this.resourceField === 'staffUuid' ? this.$t('staff.group.staff') : this.$t(`staff.group.resource`);
              const fieldLabel = key !== 'staffType' && !totals.includes(key) ? `${groupLabel} ${label}` : label; // staffType label contains the group name Staff already
              obj[fieldLabel] = entities[d.uuId][key];
            }
          }
        }
        else {
          for (const field of this.fields) {              
            // transform the key to remove any prefix
            const normalizedResult = this.normalizeKey(field);
            const normalizedField = normalizedResult.key;
            const normalizedGroup = normalizedResult.group ? normalizedResult.group : 'staff';
            
            const customField = this.customFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.companyCustomFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.locationCustomFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.stageCustomFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.projectCustomFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.departmentCustomFields.find(cf => cf.name === normalizedField || cf.name === field) ||
                                this.skillCustomFields.find(cf => cf.name === normalizedField || cf.name === field);
                                
                              
            if (customField) {
              const groupName = this.$t(`staff.group.${normalizedGroup}`);
              const label = normalizedGroup ? `${groupName} ${customField.displayName}` : customField.displayName;
              obj[label] = '';
            }
            else {
              let fieldLabel = '';
                
              if (normalizedGroup === 'company') {
                const companyLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`company.field.${normalizedField}`);
                fieldLabel = `${this.$t('staff.group.company')} ${companyLabel}`;
              }
              else if (normalizedGroup === 'location') {
                fieldLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`location.field.${this.transformKey(normalizedField)}`);
                fieldLabel = `${this.$t('staff.group.location')} ${fieldLabel}`;
              }
              else if (normalizedGroup === 'stage') {
                fieldLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`stage.field.${normalizedField}`);
                fieldLabel = `${this.$t('staff.group.stage')} ${fieldLabel}`;
              }
              else if (normalizedGroup === 'project') {
                fieldLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`project.field.${normalizedField}`);
                fieldLabel = `${this.$t('staff.group.project')} ${fieldLabel}`;
              }
              else if (normalizedGroup === 'department') {
                fieldLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`department.field.${normalizedField}`);
                fieldLabel = `${this.$t('staff.group.department')} ${fieldLabel}`;
              }
              else if (normalizedGroup === 'skill') {
                fieldLabel = normalizedField === 'identifier' ? this.$t('field.identifier_full') : this.$t(`skill.field.${normalizedField}`);
                fieldLabel = `${this.$t('staff.group.skill')} ${fieldLabel}`;
              }
              else {
                if (normalizedField === 'identifier') {
                  fieldLabel = this.$t('field.identifier_full')
                }
                else {
                  fieldLabel = this.$t(`staff.field.${this.transformKey(field)}`);
                  if (field !== 'staffType' && !totals.includes(field)) {
                    fieldLabel = `${this.resourceField === 'staffUuid' ? this.$t('staff.group.staff') : this.$t(`staff.group.resource`)} ${fieldLabel}`;
                  }
                }
              }
              
              if (!(fieldLabel in obj)) {
                obj[fieldLabel] = typeof d[field] !== 'undefined' ? d[field] : '';
              }
            }
          }
        }
                 
        if(timeline){
          d.totalFixedDuration = 0;
          d.totalEstimatedDuration = 0;
          d.totalActualDuration = 0;
          d.totalFixedCost = 0;
          d.totalEstimatedCost = 0;
          d.totalActualCost = 0;
          
          while(+from < +to){
            const projectId = this.filterByProject ? d.projectUuId : 
                            this.projectId;
            const w = this.calculateUsage(from, d, projectId);
            const m = moment();
            m.set('year', from.getFullYear());
            m.set('month', from.getMonth());
            m.set('date', from.getDate());
            const quota = d.quota ? d.quota : 1;
            d.totalFixedDuration += w.wFixed;
            d.totalEstimatedDuration += w.w;
            d.totalActualDuration += w.wActual;
            if (typeof w.fixedCost !== 'undefined') {
              d.totalFixedCost += w.fixedCost;
            }
            d.totalEstimatedCost += w.cost;
            if (typeof w.actualCost !== 'undefined') {
              d.totalActualCost += w.actualCost;
            }
            
            if (this.filter.capacity && this.resourceField === 'resourceUuid' && d.type === 'staff') {
              obj[m.format('L')] = '';
            }
            else {
              obj[m.format('L')] = this.label(this.filter.capacity ? w.defaultAvailability : this.getWorkingValue(w), w.a ? w.a * quota : 0, w.ga * quota, w.t, d.type, w.h, w.ha * quota, w, true);
            }

            /* eslint-disable no-undef */
            from = this.schedulerInstance.date.add(from, timeline.x_step, timeline.x_unit);
            /* eslint-enable no-undef */
          }
        }
        
        // add the totals
        if (`${this.$t('staff.field.totalFixedDuration')}` in obj) {
          obj[`${this.$t('staff.field.totalFixedDuration')}`] = this.staffAlloc.endsWith('hours') ? `${d.totalFixedDuration.toFixed(4).replace(/\.?0+$/, '')}h` : `${(d.totalFixedDuration / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D`;
        }
        if (`${this.$t('staff.field.totalEstimatedDuration')}` in obj) {
          obj[`${this.$t('staff.field.totalEstimatedDuration')}`] = this.staffAlloc.endsWith('hours') ? `${d.totalEstimatedDuration.toFixed(4).replace(/\.?0+$/, '')}h` : `${(d.totalEstimatedDuration / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D`;
        }
        if (`${this.$t('staff.field.totalActualDuration')}` in obj) {
          obj[`${this.$t('staff.field.totalActualDuration')}`] = this.staffAlloc.endsWith('hours') ? `${d.totalActualDuration.toFixed(4).replace(/\.?0+$/, '')}h` : `${(d.totalActualDuration / this.durationConversionOpts.hourPerDay).toFixed(4).replace(/\.?0+$/, '')}D`;
        }
        if (`${this.$t('staff.field.totalFixedCost')}` in obj) {
          obj[`${this.$t('staff.field.totalFixedCost')}`] = `${this.getCurrencySymbol(d.currency ? d.currency : 'USD')}${toFixed(d.totalFixedCost, 0)}`;
        }
        if (`${this.$t('staff.field.totalEstimatedCost')}` in obj) {
          obj[`${this.$t('staff.field.totalEstimatedCost')}`] = `${this.getCurrencySymbol(d.currency ? d.currency : 'USD')}${toFixed(d.totalEstimatedCost, 0)}`;
        }
        if (`${this.$t('staff.field.totalActualCost')}` in obj) {
          obj[`${this.$t('staff.field.totalActualCost')}`] = `${this.getCurrencySymbol(d.currency ? d.currency : 'USD')}${toFixed(d.totalActualCost, 0)}`;
        }
        
        result.rows.push(obj);
        if (d.children) {
          result = await this.getRows(result, d.children, groupings, entities);
        }
      }
      return result;
    },
    transformKey(key) {
      if (key === 'quota') {
        return 'resourceQuota';
      }
      else if (key === 'countryName') {
        return 'country';
      }
      
      return key;
    },
    normalizeKey(key) {
      if (key === 'startDate' ||
          key === 'endDate') {
        return { group: 'staff', key: key };   
      }
      
      // find the first capital letter
      for (let i = 0; i < String(key).length; i++) {
        if (String(key).charAt(i) === String(key).charAt(i).toUpperCase()) {
          const normalizedGroup = String(key).substr(0, i);
          if (normalizedGroup === 'company' ||
              normalizedGroup === 'location' ||
              normalizedGroup === 'stage' ||
              normalizedGroup === 'project' ||
              normalizedGroup === 'department' ||
              normalizedGroup === 'skill') {
            return { group: String(key).substr(0, i), key: String(key).charAt(i).toLowerCase() + String(key).substr(i + 1) }
          }
        }
      }
      return { group: null, key: null };
    },
    copyToChildren(data, field, value, entities) {
      if (data.children) {
        for (const child of data.children) {
          child[field] = value;
          if (typeof entities[child.uuId] === 'undefined') {
            entities[child.uuId] = { };
          }
          if (typeof entities[child.uuId][field] === 'undefined' ||
              (entities[child.uuId][field] === '' && value)) {
            entities[child.uuId][field] = value;
          }
          // TODO: copy the value to entities[child.uuId]
          entities = this.copyToChildren(child, field, value, entities);
        }
      }
      return entities;
    }, 
    fitToColumn(arr) {
      const ret = [];
      if (arr.length > 0) {
        for (const a of Object.keys(arr[0])) {
          ret.push({ width: 15 });
        }
      }
      return ret;
    },
    formatSpan() {
      if (this.span === 'day') {
        return 'Daily';
      }
      else if (this.span === 'week') {
        return 'Weekly';
      }
      else if (this.span === 'month') {
        return 'Monthly';
      }
      else if (this.span === 'year') {
        return 'Yearly';
      }
      return 'Daily';
    },
    fileImport() {
      this.$emit('import');
    },
    getStartHour(section, day) {
      // the staff may have multiple calendar entries for one day so we need to loop while we
      // find a matching day
      var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      let startHour = this.durationConversionOpts.startHour;
      if (section) {
        for (let i = 0; i < section.calendars.length; i++) {
          // loop through the calendar to try and find the start hour for this day
          for (const entry of section.calendars[i]) {
            if ((entry.type === days[day] || entry.type[0] === days[day]) && entry.v_StartHour) {
              if (typeof entry.startHour === 'string' &&
                  timeToMs(entry.startHour) < startHour) {
                startHour = timeToMs(entry.startHour);
              }
              else if (entry.startHour < startHour) {
                startHour = entry.startHour;
              }
            }
          }
        }
      }
      return startHour;
    },
    getEndHour(section, day) {
      var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      let endHour = this.durationConversionOpts.closeHour;
      if (section) {
        for (let i = 0; i < section.calendars.length; i++) {
          // loop through the calendar to try and find the end hour for this day
          for (const entry of section.calendars[i]) {
            if ((entry.type === days[day] || entry.type[0] === days[day]) && entry.endHour) {
              if (typeof entry.endHour === 'string' &&
                  timeToMs(entry.endHour) > endHour) {
                endHour = timeToMs(entry.endHour);
              }
              else if (entry.endHour > endHour) {
                endHour = entry.endHour;
              }
            }
          }
        }
      }
      return endHour;
    },
    getFirstHour() {
      if (this.filter.hideNonWorking) {
        return this.filter.work_hours.first_hour;
      }
      return 0;
    },
    getLastHour() {
      if (this.filter.hideNonWorking) {
        return this.filter.work_hours.last_hour;
      }
      return 23;
    },
    formatUsageMetric() {
      if (this.staffAlloc === 'estcost') {
        return this.$t('button.estcost')
      }
      else if (this.staffAlloc === 'estcostnet') {
        return this.$t('button.estcostnet');
      }
      else if (this.staffAlloc === 'fixedcost') {
        return this.$t('button.fixed_cost');
      }
      else if (this.staffAlloc === 'fixedcostnet') {
        return this.$t('button.fixed_cost_net');
      }
      else if (this.staffAlloc === 'actualcost') {
        return this.$t('button.actual_cost');
      }
      else if (this.staffAlloc === 'actualcostnet') {
        return this.$t('button.actual_cost_net');
      }
      else if (this.staffAlloc === 'fixeddurationhours') {
        return this.$t('button.fixeddurationhours');
      }
      else if (this.staffAlloc === 'fixeddurationdays') {
        return this.$t('button.fixeddurationdays');
      }
      else if (this.staffAlloc === 'actualdurationhours') {
        return this.$t('button.actualdurationhours');
      }
      else if (this.staffAlloc === 'actualdurationdays') {
        return this.$t('button.actualdurationdays');
      }
      else if (this.staffAlloc === 'hours') {
        return this.$t('button.estimateddurationhours');
      }
      else if (this.staffAlloc === 'days') {
        return this.$t('button.estimateddurationdays');
      }
      else if (this.staffAlloc === 'percent') {
        return this.$t('button.percent');
      }
      else if (this.staffAlloc === 'headcount') {
        return this.$t('button.head_count');
      }
      return '';
    },
    async fileExport() {
      const self = this;
      this.$emit('exportStart');
      setTimeout(async () => {
        const entityData = await this.getEntityData(this.treeData);
        const result = await this.getRows({ rows: [], activities: [], bookings: [], tasks: [], nonwork: [], holidays: [], added: {}}, this.treeData, {}, entityData);
        const project = self.projectName ? `<tr><td>${self.$t('project.title')}: ${self.projectName}</td></tr>` : '';
        const htmlString = `<tr><th>${self.projectName ? self.$t('staff.staff_usage') : self.resourceField !== 'resourceUuid' ? self.$t('staff.staff_planner') : self.$t('resource.planner_report')}</th></tr>
          
          ${project}
          
          <tr><td>Dates: ${moment(this.startDate).format("L")} - ${moment(this.endDate).format("L")}</td></tr>
          
          <tr><td>Timescale: ${self.formatSpan()}</td></tr>
          <tr><td>Usage Metric: ${self.formatUsageMetric()}</td></tr>
          <tr><td>Report generated on: ${moment().format("L")} ${moment().local().format("LTS")}</td></tr>
          
          <tr><td>Report generated by: ${self.$store.state.authentication.user.name} ( ${self.$store.state.authentication.user.email} )</td></tr>`
        var table = document.createElement('table');
        table.innerHTML = htmlString;
        const summary = XLSX.utils.table_to_sheet(table, { raw: true });
        const worksheet = XLSX.utils.json_to_sheet(result.rows);
        worksheet['!cols'] = self.fitToColumn(result.rows);
        const activityWorksheet = XLSX.utils.json_to_sheet(result.activities);
        activityWorksheet['!cols'] = self.fitToColumn(result.activities);
        const bookingWorksheet = XLSX.utils.json_to_sheet(result.bookings);
        bookingWorksheet['!cols'] = self.fitToColumn(result.bookings);
        const taskWorksheet = XLSX.utils.json_to_sheet(result.tasks);
        taskWorksheet['!cols'] = self.fitToColumn(result.tasks);
        const nonworkWorksheet = XLSX.utils.json_to_sheet(result.nonwork);
        nonworkWorksheet['!cols'] = self.fitToColumn(result.nonwork);
        const holidaysWorksheet = XLSX.utils.json_to_sheet(result.holidays);
        holidaysWorksheet['!cols'] = self.fitToColumn(result.holidays);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, summary, self.$t('staff.summary'));
        
        XLSX.utils.book_append_sheet(workbook, worksheet, self.$t('staff.usage'));
        
        if (self.filter.booking) {
          XLSX.utils.book_append_sheet(workbook, bookingWorksheet, self.$t('booking.title'));
        }
        if (self.filter.activity) {
          XLSX.utils.book_append_sheet(workbook, activityWorksheet, self.$t('activity.title'));
        }
        if (self.filter.task) {
          XLSX.utils.book_append_sheet(workbook, taskWorksheet, self.$t('task.title'));
        }
        if (self.filter.vacation && self.resourceField !== 'resourceUuid') {
          XLSX.utils.book_append_sheet(workbook, nonworkWorksheet, self.$t('vacation.title'));
          XLSX.utils.book_append_sheet(workbook, holidaysWorksheet, self.$t('vacation.holidays'));
        }
        XLSX.writeFile(workbook, "Planner.xlsx", { compression: true });
        this.$emit('exportEnd');
      }, 500);
    },
    costAlert(w, quota) {
      if ((this.staffAlloc === 'actualcost' ||
          this.staffAlloc === 'actualcostnet') &&
          w.actualCost > (w.basecost * quota)) {
        return true;
      }
      /*else if ((this.staffAlloc === 'fixedcost' ||
          this.staffAlloc === 'fixedcostnet') &&
          w.fixedCost < (w.basecost * quota)) {
        return true;
      }*/
      else if ((this.staffAlloc === 'estcost' ||
          this.staffAlloc === 'estcostnet') &&
          w.cost > (w.basecost * quota)) {
        return true;
      }
      return false;
    },
    actualCostAlert(event) {
      if(event.actualCost != null) {
        const fixedCost = event.fixedCost > 0? event.fixedCost : 0;
        if(event.actualCost > fixedCost && fixedCost !== 0) {
          return true;
        } else if(event.estimatedCost && event.actualCost > event.estimatedCost) {
          return true;
        }
      }
      return false;
    },
    actualCostAlertMsg(event) {
      if(event.actualCost) {
        const fixedCost = event.fixedCost ? event.fixedCost : 0;
        const isCurrencyCodeValid = event.currencyCode && event.currencyCode.trim().length > 0;
        if(event.actualCost > fixedCost && fixedCost !== 0) {
          const diff = isCurrencyCodeValid? costFormatAdv(event.actualCost - fixedCost, event.currencyCode) : `$${costFormat(event.actualCost - fixedCost)}`;
          return this.$t('task.alert.actual_cost_exceeds_fixed_cost', [diff]);
        } else if(event.estimatedCost && event.actualCost > event.estimatedCost) {
          const diff = isCurrencyCodeValid? costFormatAdv(event.actualCost - event.estimatedCost, event.currencyCode) :  `$${costFormat(event.actualCost - event.estimatedCost)}`;
          return this.$t('task.alert.actual_cost_exceeds_estimated_cost', [diff]);
        }
      }
      return '';
    },
    fixedDurationAlertMsg(event) {
      if(event.fixedDuration != null) {
        const duration = typeof event.te !== 'undefined' ? event.te : event.duration;
        if(event.actualDuration > event.fixedDuration) {
          const diff = convertDurationToDisplay((event.actualDuration - event.fixedDuration) / 60000, 'D', this.durationConversionOpts);          return this.$t('task.alert.actual_duration_exceeds_fixed_duration', [diff]);
        } else if(duration > event.fixedDuration) {
          const diff = convertDurationToDisplay((duration - event.fixedDuration) / 60000, 'D', this.durationConversionOpts);

          return this.$t('task.alert.estimated_duration_exceeds_fixed_duration', [diff]);
        }
      }
      return '';
    },
    fixedDurationAlert(event) {
      if (event.fixedDuration && !['0','0Y','0M','0W','0D','0h','0m'].includes(event.fixedDuration)) {
        const duration = typeof event.te !== 'undefined' ? event.te : event.duration;
        return event.fixedDuration < duration || event.fixedDuration < event.actualDuration;
      }
      return false;
    },
    updateParentData(sectionParentUuidList, utcStart, utcEnd, duration, taskId, staffUuId, listName, task) {
      if (sectionParentUuidList[0] !== null) {
        let parentSection = this.schedulerInstance.getSection(sectionParentUuidList[0]);
        const parentUuid = parentSection ? parentSection.$parent : null;
        
        if (parentSection && parentSection.unfilteredChildren) {
          for (let parentIndex = 1; parentIndex < sectionParentUuidList.length; parentIndex++) {
            const ps = parentSection.unfilteredChildren.find(p => p.uuId === sectionParentUuidList[parentIndex].slice(sectionParentUuidList[parentIndex].length - 36));
            if (ps) {
              parentSection = ps;
            }
            else {
              break;
            }
          }
          
          if (parentSection) {
            const childSection = parentSection.unfilteredChildren.find(c => c.uuId === staffUuId);
            if (childSection) {
              if (task === null) { // remove it
                const index = childSection[listName].findIndex(t => t.uuId === taskId);
                if (index !== -1) {
                  childSection[listName].splice(index, 1);
                }
              }
              else {
                const cfiltered = childSection[listName].filter(t => t.uuId === taskId);
                if (cfiltered.length > 0) {
                  cfiltered[0].begin = utcStart;
                  cfiltered[0].until = utcEnd;
                  cfiltered[0].duration = cfiltered[0].te = duration;
                }
                else {
                  childSection[listName].push(task);
                }
              }
            }
          }
          this.updateParentData([parentUuid, ...sectionParentUuidList], utcStart, utcEnd, duration, taskId, staffUuId, listName, task)
        }
      }
    },
    updateTaskDates(mode, event, originalSection) {
 
        // Need to update the dates of the task so that the working hours updates for the staff
        let staffSection = this.schedulerInstance.getSection(event.section_id);
        if (staffSection.duplicateStaff && staffSection.duplicateStaff.find(s => s.uuId === event.staffUuid)) {
          staffSection = staffSection.duplicateStaff.find(s => s.uuId === event.staffUuid);
        }
        
        // We need to move the task to the other staff member
        const listName = event.type === 'task' ? 'taskList' :
                         event.type === 'activity' ? 'activityList' :
                         event.type === 'vacation' ? 'calendarList' :
                         'bookingList';
        if (mode !== 'new-size' && staffSection) {
          if (!staffSection[listName]) {
            staffSection[listName] = [];
          }
        
          const taskId = event.id.slice(event.id.length - 36);
          if (event[this.resourceField] !== originalSection) {
            const otherStaff = this.schedulerInstance.getSection(originalSection);
            const index = otherStaff[listName].findIndex(t => t.uuId === taskId);
            if (index !== -1) {
              // remove the task from the original staff
              const tasks = otherStaff[listName].splice(index, 1);
              this.updateParentData([otherStaff.$parent], 0, 0, 0, taskId, otherStaff.uuId, listName, null);
              
              staffSection[listName].push(...tasks);
            }
          }
          
          const filtered = staffSection[listName].filter(t => t.uuId === taskId);
          if (filtered.length > 0) {
            // We need UTC dates for the update
            let utcStart = Date.UTC(event.start_date.getFullYear(), event.start_date.getMonth(),
                      event.start_date.getDate(), event.start_date.getHours(),
                      event.start_date.getMinutes(), event.start_date.getSeconds());
            let utcEnd = Date.UTC(event.end_date.getFullYear(), event.end_date.getMonth(),
                      event.end_date.getDate(), event.end_date.getHours(),
                      event.end_date.getMinutes(), event.end_date.getSeconds());
            if (listName !== 'calendarList') {
              filtered[0].begin = utcStart;
              filtered[0].until = utcEnd;
            }
            else {
              filtered[0].startDate = utcStart;
              filtered[0].endDate = utcEnd;
            }
            
            // if the staff has changed then clear the actual duration
            if (originalSection !== event[this.resourceField]) {
              filtered[0].actualDuration = 0;
            }
            
            // update the duration
            let calendar = null;
            if (staffSection.calendars) {
              const cals = [];
              const names = [ 'staff', 'location', 'base_calendar'];
              
              for (let i = staffSection.calendars.length - 1; i >= 0; i--) {
                const c = staffSection.calendars[i];
                for (const calEntry of c) {
                  if (typeof calEntry.startDate !== 'undefined' &&
                      typeof calEntry.endDate !== 'undefined') {
                    //calEntry.startDate = moment(calEntry.startDate).format('YYYY-MM-DD');
                    //calEntry.endDate = moment(calEntry.endDate).format('YYYY-MM-DD');
                  }
                }
                cals.push({ name: names[i], calendarList: c });
              }
              calendar = transformCalendar(processCalendar(cals));
            }
            else {
              calendar = DEFAULT_CALENDAR;
            }
            
            const durationResult = recalcDuration(event.start_date, 
                                                  event.end_date, 
                                                  calendar, 
                                                  this.span, 
                                                  this.staffAlloc, 
                                                  mode, 
                                                  `${event.te / this.durationConversionOpts.hourPerDay}D`,
                                                  event.lockDuration,
                                                  this.durationConversionOpts);
                                                  
            // we have two values for duration te (task estimated duration) and duration
            filtered[0].duration = filtered[0].te = convertDisplayToDuration(durationResult.value, this.durationConversionOpts).value * 60000;
            utcStart = convertDate(durationResult.startDate, durationResult.startTime);
            utcEnd = convertDate(durationResult.closeDate, durationResult.closeTime);
            
            
            // update clones
            if (event.clones) {
              this.updateClones(event.clones, taskId, utcStart, utcEnd);
            }
            
            this.updateParentData([staffSection.$parent], utcStart, utcEnd, filtered[0].duration, taskId, staffSection.uuId, listName, filtered[0]);
            
          }
        }
    },
    //setTreeData is called in PlannerStaff
    setTreeData(data) {
      this.treeData = data;
      if (this.initialized) {
        /* eslint-disable no-undef */
        this.settingView = true;
        this.schedulerInstance.config.event_yoff = this.filter.usage ? 26 : 0;
        
        // this.schedulerInstance._not_render = true;
        if (this.filter.unallocated) {
          // get all events in the time period
          const evs = this.schedulerInstance.getEvents(new Date(this.unallocStart), new Date(this.unallocEnd));
          // only add sections with no events
          this.schedulerInstance.updateCollection("sections", this.treeData.filter(d => evs.filter(e => e.section_id === d.key).length === 0));
        }
        else {
          this.schedulerInstance.updateCollection("sections", this.treeData);
        }
        //holdUpdateUntilTreeDataChanged is introduced to reduce the number of call to updateData() when loading view settings in parent components (e.g plannerStaff/plannerResource).
        //Setting changes may trigger the updateData() multiple times. Rendering performance is impacted and rendering glitch may occur.
        if (this.holdUpdateUntilTreeDataChanged) {
          this.updateData();
          this.schedulerInstance.parse(this.data);
          
          this.$emit('update:holdUpdateUntilTreeDataChanged', false);
        } 
        
        this.settingView = false;
        
        this.$emit('update:skipProcessNodes', false);
        /* eslint-enable no-undef */
      }
    },
    getDurationConversionOpts() {
      return this.$store.dispatch('data/configSchedule').then(value => {
        this.durationConversionOpts = extractDurationConversionOpts(value);
      })
      .catch(e => {
        console.error(e); //eslint-disable-line no-console
      });
    }
    
  }
}
</script>
<style>
  .dhx_matrix_cell div.load-marker{
    position: absolute;
    width: 100%;
    text-align: center;
    color: white;
    font-size: 12px;
    line-height: 25px;
    height: 25px;
  }

  .load-marker-no{
    opacity: 0.5;
    background-color: var(--scheduler-cell-disabled);
  }
  .load-marker-low{
    color: var(--white);
     background-color: var(--green-200);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-med{
    color: var(--white);
     background-color: var(--green-400);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-high{
    color: var(--white);
     background-color: var(--green-600);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-full {
    color: var(--white);
     background-color: var(--green-800);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-over {
    background-color: var(--red-400);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-over2 {
    background-color: var(--red-600);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-over3 {
    background-color: var(--red-800);
     cursor: pointer;
     z-index: 2;
  }
  .load-marker-not-employed {
    background-color: #000000;
    height: 100% !important;
  }
  .load-marker-bad-assign {
    background-color: #000000;
     z-index: 2;
  }
  .load-marker-weekend {
    opacity: 0.5;
    background-color: var(--scheduler-cell-disabled);
  }
  
  .dhx_matrix_scell div {
    white-space: nowrap;
  }
  
  .dhx_matrix_now_time {
    border-left: 2px solid var(--gantt-start-end-marker-bg) !important;
  }

  .dhx_matrix_scell {
    font-size: 12px;
    font-weight: 400;
  }
  
  .dhx_scell_level7 {
    padding-left: 110px;
  }
  .dhx_scell_level6 {
    padding-left: 95px;
  }
  .dhx_scell_level5 {
    padding-left: 80px;
  }
  .dhx_scell_level4 {
    padding-left: 65px;
  }
  
  .dhx_cal_event_line .dhx_event_resize {
    z-index: 1;
    top: 3px;
    height: 14px !important;
  }
  
  .dhx_cal_event_line {
    color: var(--white);
    line-height: 22px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 300;
    border: none;
    margin-top: 1px;
    background: transparent;
  }
  
  .event_selected {
    box-shadow: 0 0 0 0.2rem var(--scheduler-focus-shadow);
  }
  
  .event_new {
    border-color: var(--gantt-task) !important;
    border: 1px solid var(--gantt-task);
  }
  
  .dhx_timeline_label_row {
    background-color: var(--ag-background-color);
  }
  
  .dhx_data_table.folder .dhx_matrix_cell, .dhx_matrix_scell.folder {
    background-color: var(--ag-background-color);
    border-right: 1px solid var(--ag-row-border-color);
  }
  
  .dhx_cal_header div.dhx_bottom_scale_container .dhx_scale_bar {
    border-left-color: var(--ag-row-border-color);
  }
  
  .dhx_cal_header {
    background: #cecece;
    border-top: none;
  }
  
  .dhx_timeline_scale_header.dhx_timeline_second_scale {
    border-right: none;
  }
  
  .dhx_scale_bar.dhx_second_scale_bar {
    border-bottom-color: var(--ag-row-border-color);
  }
    
  .dhx_cal_data .dhx_timeline_table_wrapper div {
    border-color: var(--ag-row-border-color);
  }

  .dhx_cal_data {
    border-top: none;
  }
  
  .dhx_timeline_label_wrapper, .dhx_timeline_scale_header,
  .dhx_cal_container {
    background-color: var(--surface-bg) !important;
    /* background-color: var(--bs-border); */
  }
  
  .dhx_cal_navline .dhx_cal_date {
    color: var(--ag-data-color);
  }
  
  .dhx_matrix_scell.folder .dhx_scell_name {
    float: none;
    text-transform: none;
  }
  
  .dhx_cal_header,
  .dhx_cal_navline {
    z-index: 0;
  }
  
  .dhx_cal_navline {
    display: none;
  }
  
  .dhx_matrix_scell.folder .dhx_scell_expand {
    margin: 0 3px;
    display: none !important;
  }
  
  .dhx_matrix_cell div, .dhx_matrix_scell div {
    text-align: left;
  }
  
  .dhx_matrix_scell.folder.opened .dhx_scell_expand:after,
  .dhx_matrix_scell.folder.closed .dhx_scell_expand:after {
    background: none;
  }
  
  .dhx_matrix_scell, .dhx_timeline_scale_header {
    color: var(--ag-data-color);
  }
  
  .dhx_scale_bar .dhx_second_scale_bar {
    top: 1px;
    border-top: none;
  }
  
  .dhx_matrix_cell {
    background-color: var(--ag-odd-row-background-color);
    border-color: var(--ag-row-border-color);
  }
  
  .dhx_scale_bar,
  .dhx_timeline_scale_header {
    background-color: var(--gantt-scale-line2-cell-bg);
    color: var(--ag-header-foreground-color);
    font-size: 12px;
    font-weight: bold;
  }
  
  .dhx_focus_slot {
    background-color: var(--selection-background-color);
    border: 2px solid;
    border-color: var(--selection-border-color) !important;
    border-style: var(--selection-border-style);
    opacity: 1;
    z-index: 3;
  }
  
  .dhx_scale_bar.dhx_second_scale_bar {
    border-top-color: var(--gantt-scale-line2-cell-bg) !important;
  }
  .dhx_cal_header div .dhx_scale_bar:not(.dhx_second_scale_bar) {
    border-top: 1px solid var(--border-light) !important;
  }
  
  .dhx_matrix_cell .load-marker-weekend {
    height: 100% !important;
  }
  
  .dhx_data_table.folder .dhx_matrix_cell, .dhx_matrix_scell.folder {
    cursor: auto;
  }
  
  .dhx_cal_header div .dhx_scale_bar.dhx_second_scale_bar {
    border-bottom: 1px solid transparent !important;
  }
  
  .dhx_cal_container .dhx_cal_header.dhx_second_cal_header .dhx_scale_bar.dhx_second_scale_bar {
    border-left: 1px solid var(--ag-header-column-separator-color);
  }
  
  .dhx_cal_container .dhx_cal_header.dhx_second_cal_header .dhx_scale_bar {
    border-left: 1px solid var(--ag-header-column-separator-color);
    border-color: var(--ag-header-column-separator-color);
  }
  
  .tag {
    border-radius: 3px;
    vertical-align: text-bottom;
    display: inline-block;
    color: var(--white);
    font-size: 0.8em;
    padding: 0 5px 0 5px;
    height: 15px;
    line-height: 15px;
    margin-right: 3px;
    min-width: 16px;
  }

.tag-company {
  background-color: var(--deep-purple-400);
}

.tag-department {
  background-color: var(--blue-900);
}

.tag-skill {
  background-color: var(--deep-purple-300);
}

.tag-stage {
  background-color: var(--brown-400);
}

.tag-project {
  background-color: var(--pink-800);
}

.tag-staff-generic {
  background-color: var(--blue);
}

.tag-location {
  background-color: var(--teal);
}

.tag-staff {
  background-color: var(--pumpkin);
}

.tag-task {
  background-color: var(--cyan-600);
}

.tag-activity {
  background-color: var(--activity);
}

.tag-vacation {
  background-color: var(--blue-gray-300);
}

.tag-new {
  background-color: #777;
}

.tag-booking {
  background-color: var(--booking);
}

.tag-resource {
  background-color: var(--light-green-900);
}

.cell-tooltip,
.dhtmlXTooltip.tooltip[role=tooltip] {
  box-shadow: var(--surface-shadow);
  border-radius: 0.25rem;
  pointer-events: none;
  transition: opacity 1s;
  background-color: var(--popover-body-bg) !important;
  color: var(--popover) !important;
  font-size: 12px;
  z-index: 10000;
}

.dhtmlXTooltip.tooltip[role=tooltip] {
  border: none;
  padding: 0;
}

.cell-tooltip p,
.task-tooltip-body p {
  margin: 5px;
  white-space: nowrap;
}

.task-tooltip-body {
  padding-left: 3px;
  padding-right: 3px;
}

.header {
  vertical-align: center;
  margin-right: 3px;
}

.header-box {
  background-color: var(--gray-500);
  color: var(--white);
  border-bottom: 1px solid var(--border-medium);
  display: flex;
  align-items: center;
  padding-top: 3px;
  padding-bottom: 3px;
  padding-left: 3px;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.add-event.btn-secondary,
.edit-event.btn-secondary,
.delete-event.btn-secondary,
.copy-event.btn-secondary,
.paste-event.btn-secondary,
.staff-event.btn-secondary,
.export-event.btn-secondary {
  background-color: transparent;
  border-color: transparent;
  color: var(--grid-toolbar-button);
  padding: 2px 6px;
  font-size: 1rem;
}

.add-event.btn-secondary.disabled,
.edit-event.btn-secondary.disabled,
.delete-event.btn-secondary.disabled,
.copy-event.btn-secondary.disabled,
.paste-event.btn-secondary.disabled,
.staff-event.btn-secondary.disabled,
.export-event.btn-secondary.disabled {
  background-color: transparent;
  border-color: transparent;
  color: var(--grid-toolbar-button);
  opacity: 0.4;
}

.add-event.btn-secondary:focus,
.edit-event.btn-secondary:focus,
.delete-event.btn-secondary:focus,
.copy-event.btn-secondary:focus,
.paste-event.btn-secondary:focus,
.staff-event.btn-secondary:focus,
.export-event.btn-secondary:focus {
  box-shadow: none;
}

.add-event.btn-secondary:hover,
.edit-event.btn-secondary:hover,
.delete-event.btn-secondary:hover,
.copy-event.btn-secondary:hover,
.paste-event.btn-secondary:hover,
.staff-event.btn-secondary:hover,
.export-event.btn-secondary:hover {
  color: var(--grid-toolbar-button);
  background-color: transparent;
  border-color: transparent;
}

.dhx_matrix_cell div.today,
.dhx_scale_bar.today {
  background-color: var(--today);
  height: 100%;
  z-index: 0;
}

.partial-start .dhx_event_resize_start {
  display: none !important;
}

.partial-end .dhx_event_resize_end {
  display: none !important;
}

.scheduler-actions {
  background: var(--surface-bg);
  border-bottom: 1px solid var(--bs-border);
  border-left: 1px solid var(--bs-border);
  border-right: 1px solid var(--bs-border);
  display: flex;
}

@media only screen and (max-width: 600px) {
  .scheduler-actions {
    display: block;
  }
  .dhx_matrix_scell{
    display:none !important;
  }
}

.filter-flex-grow-container {
  flex: 1;
  margin-top: -2px;
}

.event-text {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-x: hidden;
  margin-left: 12px;
  margin-right: 12px;
  position: relative;
  top: 0;
}

.readonly-icon {
  margin-right: 5px;
}

.staff-tooltip-skills {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-x: hidden;
  max-width: 200px;
}

::-webkit-scrollbar-corner {
  background: var(--surface-bg);
}

.img-avatar.smaller {
  margin: 4px !important;
  position: relative;
  height: 35px !important;
}

.avatar-align {
  vertical-align: top;
}

.tree-icon {
  width: 10px;
  height: 10px;
  color: var(--ag-secondary-foreground-color);
  margin: auto;
  margin-right: 5px;
  cursor: pointer;
}

.hidden-tree-icon {
  display: none !important;
}

.scheduler-odd-row {
  background-color: var(--ag-odd-row-background-color) !important;
  height: 100% !important;
}

.scheduler-even-row {
  background-color: var(--ag-background-color) !important;
  height: 100% !important;
}

.scheduler-border-part {
    border-bottom: 1px solid var(--bs-border);
    border-left: 1px solid var(--bs-border);
    border-right: 1px solid var(--bs-border);
}

.scheduler-divider {
  padding-top: 4px;
  font-size: 14px;
  display: inline-block;
}

.noimg-avatar {
  display:inline-block;
  font-size:1em;
  width:30px;
  height:30px;
  line-height:2.5em;
  text-align:center !important;
  border-radius:50%;
  vertical-align:middle;
  margin-right:4px;
  color:white;
  }
  
  .scheduler-tag-link {
    color: var(--link-text);
  }
  
  .scheduler-tag-nolink {
    color: var(--link-text);
    display: inline;
  }
  
  .tree-minimizer {
    background-color: transparent;
    position: absolute;
    top: 10px;
    left: 0;
    height: 25px;
    z-index: 10;
    border: none;
  }
  
  .tree-minimizer-expanded {
    left: 270px;
  }
  
  .tree-minimizer:focus {
    outline-style: none;
  }
  
  .tree-minimizer-icon {
    color: var(--ag-header-foreground-color);
  }
  
  .pointer-events-none {
    pointer-events: none;
  }
  
  .section-expand {
    display: inline-block;
  }
    
  .no-rows-overlay {
    position: absolute;
    display: flex;
    height: 100%;
    z-index: 3;
    width: 100%;
    pointer-events: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .loading-overlay {
    position: absolute;
    display: flex;
    height: 100%;
    z-index: 3;
    width: 100%;
    pointer-events: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--surface-bg);
  }

  .scheduler .no-rows-overlay,
  .scheduler .loading-overlay {
    font-size: 12px;
  }
  
  .type-color {
    padding-left: 0;
    padding-right: 0;
    height: 16px;
    top: 1px;
    position: relative;
    border-radius: 50%;
    margin-right: 5px;
  }
  
  .warning-icon {
    margin-right: 5px;
    margin-left: 1px;
  }
  
  .dhx_cal_event_line.readonly {
    cursor: default;
  }

  .dhx_cal_header {
    border-bottom: 1px solid var(--border-light);
    top: 0px !important;
  }
</style>
